// src/atomWithPagination.ts
import { atom, useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback, useRef, useState } from "react";
var DEFAULT_PAGE_SIZE = 50;
var NOOP_ATOM = atom(void 0);
var NOOP_SETTER = atom(null, () => {
});
function atomWithLoadMore(factory, options) {
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const limitAtom = atom(pageSize);
  const filterAtom = options?.initialFilter !== void 0 ? atom(options.initialFilter) : void 0;
  const sortAtom = atom(void 0);
  const dataAtom = factory({ limitAtom, filterAtom, sortAtom });
  const meta = {
    limitState: atom((get) => get(limitAtom)),
    loadMoreAction: atom(null, (get, set) => {
      set(limitAtom, get(limitAtom) + pageSize);
    }),
    pageSize,
    filterState: filterAtom ? atom((get) => get(filterAtom)) : void 0,
    setFilterAction: filterAtom ? atom(null, (_get, set, filter) => {
      set(filterAtom, filter);
      set(limitAtom, pageSize);
    }) : void 0,
    sortState: atom((get) => get(sortAtom)),
    // Re-sorting reshuffles which rows are on which page, so the loaded pages are meaningless
    // against the new order — same reset a filter change does.
    setSortAction: atom(null, (_get, set, sort) => {
      set(sortAtom, sort);
      set(limitAtom, pageSize);
    })
  };
  return Object.assign(dataAtom, { __loadMore: meta });
}
function useLoadMore(pagedAtom) {
  const { limitState, loadMoreAction, pageSize, filterState, setFilterAction, sortState, setSortAction } = pagedAtom.__loadMore;
  const loadableValue = useAtomValue(loadable(pagedAtom));
  const freshData = loadableValue.state === "hasData" ? loadableValue.data : [];
  const currentLimit = useAtomValue(limitState);
  const loadMore = useSetAtom(loadMoreAction);
  const filter = useAtomValue(filterState ?? NOOP_ATOM);
  const setFilter = useSetAtom(setFilterAction ?? NOOP_SETTER);
  const sort = useAtomValue(sortState);
  const setSort = useSetAtom(setSortAction);
  const cachedDataRef = useRef([]);
  if (loadableValue.state === "hasData") {
    cachedDataRef.current = freshData;
  }
  const data = loadableValue.state === "loading" && currentLimit > pageSize ? cachedDataRef.current : freshData;
  return {
    data,
    isLoading: loadableValue.state === "loading" && currentLimit <= pageSize,
    hasError: loadableValue.state === "hasError",
    hasMore: data.length >= currentLimit,
    loadMore,
    filter,
    setFilter,
    sort,
    setSort
  };
}
function atomWithCursor(options) {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const cursorAtom = atom(void 0);
  const filterAtom = options.initialFilter !== void 0 ? atom(options.initialFilter) : void 0;
  const dataAtom = options.factory({ cursorAtom, filterAtom });
  const meta = {
    setCursorAction: atom(null, (_get, set, cursor) => {
      set(cursorAtom, cursor);
    }),
    countAtom: atom(options.countFn),
    pageSize,
    filterState: filterAtom ? atom((get) => get(filterAtom)) : void 0,
    setFilterAction: filterAtom ? atom(null, (_get, set, filter) => {
      set(filterAtom, filter);
      set(cursorAtom, void 0);
    }) : void 0
  };
  return Object.assign(dataAtom, { __cursor: meta });
}
function useCursorPaging(pagedAtom, getCursor) {
  const meta = pagedAtom.__cursor;
  const loadableValue = useAtomValue(loadable(pagedAtom));
  const data = loadableValue.state === "hasData" ? loadableValue.data : [];
  const totalCountLoadable = useAtomValue(loadable(meta.countAtom));
  const totalCount = totalCountLoadable.state === "hasData" ? totalCountLoadable.data : void 0;
  const setCursor = useSetAtom(meta.setCursorAction);
  const [pageIndex, setPageIndex] = useState(0);
  const cursorStackRef = useRef([void 0]);
  const totalPages = totalCount ? Math.ceil(totalCount / meta.pageSize) : void 0;
  const goNext = useCallback(() => {
    const lastItem = data.at(-1);
    if (!lastItem) return;
    const nextCursor = getCursor(lastItem);
    const newIndex = pageIndex + 1;
    if (newIndex >= cursorStackRef.current.length) {
      cursorStackRef.current.push(nextCursor);
    }
    setPageIndex(newIndex);
    setCursor(nextCursor);
  }, [data, pageIndex, setCursor, getCursor]);
  const goPrev = useCallback(() => {
    const newIndex = pageIndex - 1;
    setPageIndex(newIndex);
    setCursor(cursorStackRef.current[newIndex]);
  }, [pageIndex, setCursor]);
  const filter = useAtomValue(meta.filterState ?? NOOP_ATOM);
  const rawSetFilter = useSetAtom(meta.setFilterAction ?? NOOP_SETTER);
  const setFilter = useCallback(
    (f) => {
      rawSetFilter(f);
      setPageIndex(0);
      cursorStackRef.current = [void 0];
    },
    [rawSetFilter]
  );
  return {
    data,
    isLoading: loadableValue.state === "loading",
    hasError: loadableValue.state === "hasError",
    page: pageIndex + 1,
    totalPages,
    totalCount,
    hasNext: data.length >= meta.pageSize,
    hasPrev: pageIndex > 0,
    goNext,
    goPrev,
    filter,
    setFilter
  };
}

// src/atomWithSnapshot.ts
import {
  getDoc,
  getDocs,
  onSnapshot
} from "firebase/firestore";
import { atom as atom2 } from "jotai";
import { startTransition } from "react";

// src/dao/utils.ts
async function unwrapValue(get, value) {
  if (value === null || value === void 0) {
    return value;
  }
  if (typeof value === "object" && "read" in value) {
    return await get(value);
  }
  return value;
}
async function unwrapAll(get, values) {
  const keys = Object.keys(values);
  const resolved = await Promise.all(keys.map((key) => unwrapValue(get, values[key])));
  return Object.fromEntries(keys.map((key, index) => [key, resolved[index]]));
}
function parseWithSchema(data, schema, path, log2) {
  if (!data) {
    return void 0;
  }
  const result = schema.safeDecode(data);
  if (result.success) {
    return result.data;
  }
  const pathString = typeof path === "function" ? `/${path()}` : `/${path}`;
  (log2 || console).error(
    `Schema validation failed at path: ${pathString}${typeof process !== "undefined" && process.env?.EXPO_PUBLIC_ROCK_APP_ENV === "local" ? ` issues: ${JSON.stringify(result.error.issues, null, 2)}` : ""}`,
    { issues: result.error.issues, path: pathString, rawData: data }
  );
  return void 0;
}

// src/atomWithSnapshot.ts
var snapshotEpochAtom = atom2(0);
var refreshSnapshotsAtom = atom2(null, (_get, set) => {
  set(snapshotEpochAtom, (epoch) => epoch + 1);
});
function atomWithDocument(reference, options) {
  const { useTransition, schema, log: log2 } = options;
  const getRef = async (get) => typeof reference === "function" ? await reference(get) : reference;
  const proxyAtom = atom2(async (get) => {
    get(snapshotEpochAtom);
    const ref = await getRef(get);
    const dataAtom = atom2(
      new Promise((resolve, reject) => {
        if (ref) {
          getDoc(ref).then((snap) => {
            const rawData = snap.data();
            const parsed = parseWithSchema(rawData, schema, () => ref.path, log2);
            resolve(parsed);
          }).catch(reject);
        } else {
          resolve(void 0);
        }
      })
    );
    dataAtom.onMount = (setAtom) => {
      if (ref) {
        const unsub = onSnapshot(
          ref,
          (snap) => {
            const rawData = snap.data();
            const parsed = parseWithSchema(rawData, schema, () => ref.path, log2);
            if (useTransition) {
              startTransition(() => {
                setAtom(parsed);
              });
            } else {
              setAtom(parsed);
            }
          },
          (err) => {
            log2.error(`Error onSnapshot for ref: ${ref.path}`, { err });
            const rejected = Promise.reject(err);
            rejected.catch(() => void 0);
            setAtom(rejected);
          }
        );
        return () => {
          unsub();
        };
      }
    };
    return dataAtom;
  });
  return atom2(async (get) => get(await get(proxyAtom)));
}
function atomWithQuery(reference, options) {
  const { useTransition, schema, log: log2 } = options;
  const getRef = (get) => typeof reference === "function" ? reference(get) : reference;
  const toResult = (snap, ref) => {
    return snap.docs.map((d) => {
      const rawData = { ...d.data(), id: d.id };
      return parseWithSchema(
        rawData,
        schema,
        () => `${ref?._query?.path?.segments?.join("/") ?? "unknown"}/${d.id}`,
        log2
      );
    }).filter((item) => item !== void 0);
  };
  const proxyAtom = atom2(async (get) => {
    get(snapshotEpochAtom);
    const ref = await getRef(get);
    const dataAtom = atom2(
      new Promise((resolve, reject) => {
        if (ref) {
          getDocs(ref).then((snap) => resolve(toResult(snap, ref))).catch(reject);
        } else {
          resolve([]);
        }
      })
    );
    dataAtom.onMount = (setAtom) => {
      if (ref) {
        return onSnapshot(
          ref,
          (snap) => {
            const result = toResult(snap, ref);
            if (useTransition) {
              startTransition(() => {
                setAtom(result);
              });
            } else {
              setAtom(result);
            }
          },
          (err) => {
            const path = ref?._query?.path?.segments?.join("/") ?? "unknown query";
            log2.error(`Error onSnapshot for query: ${path}`, { err });
            const rejected = Promise.reject(err);
            rejected.catch(() => void 0);
            setAtom(rejected);
          }
        );
      }
    };
    return dataAtom;
  });
  return atom2(async (get) => {
    return get(await get(proxyAtom));
  });
}

// src/clientAdapter.ts
import { matchesExpected } from "@rock/dao";

// src/db.ts
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { getAuth } from "firebase/auth";
import {
  collection as fsCollection,
  collectionGroup as fsCollectionGroup,
  doc as fsDoc,
  initializeFirestore
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
var app;
var auth;
var db;
var storage;
function initializeFirebase(config, appCheckSiteKey) {
  app = initializeApp(config);
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true
    });
  } catch (err) {
    console.error("[@rock/db-admin] Error initializing App Check", err);
  }
  auth = getAuth(app);
  db = initializeFirestore(app, { ignoreUndefinedProperties: true });
  storage = getStorage(app);
}
var createId = () => {
  if (!db) {
    console.error("[@rock/db-admin] You must call initializeFirebase() before using createId()");
  }
  return fsDoc(fsCollection(db, "_")).id;
};
function collection(ref, path) {
  if (!db) {
    console.error("[@rock/db-admin] You must call initializeFirebase() before using collection()");
  }
  if (typeof ref === "string") {
    return fsCollection(db, ref);
  } else {
    return fsCollection(ref, path);
  }
}

// src/clientAdapter.ts
import {
  arrayRemove,
  arrayUnion,
  collection as fsCollection2,
  deleteDoc as fsDeleteDoc,
  deleteField,
  doc as fsDoc2,
  getDoc as getDoc2,
  runTransaction,
  setDoc,
  updateDoc,
  writeBatch
} from "firebase/firestore";

// src/onlineState.ts
var online = true;
var log;
function setOnlineStatus(value) {
  online = value;
}
function isOnline() {
  return online;
}
function setOfflineLogger(logger) {
  log = logger;
}
function logOfflineWrite(context, err) {
  log?.debug(`Offline write (fire-and-forget): ${context}`, { err });
}

// src/clientAdapter.ts
var clientAdapter = {
  async getDoc(path) {
    const snap = await getDoc2(fsDoc2(db, path));
    return snap.exists() ? snap.data() : void 0;
  },
  async setDoc(path, data, options) {
    const writePromise = setDoc(fsDoc2(db, path), data, options?.merge ? { merge: true } : {});
    if (!isOnline()) {
      writePromise.catch((err) => logOfflineWrite(`setDoc ${path}`, err));
      return;
    }
    await writePromise;
  },
  async updateDoc(path, updates) {
    const writePromise = updateDoc(fsDoc2(db, path), updates);
    if (!isOnline()) {
      writePromise.catch((err) => logOfflineWrite(`updateDoc ${path}`, err));
      return;
    }
    await writePromise;
  },
  async compareAndSetDoc(path, { expect, updates }) {
    return await runTransaction(db, async (tx) => {
      const ref = fsDoc2(db, path);
      const snap = await tx.get(ref);
      if (!snap.exists()) return false;
      if (!matchesExpected(snap.data(), expect)) return false;
      tx.update(ref, updates);
      return true;
    });
  },
  async commitBatch(writes) {
    const batch = writeBatch(db);
    for (const write of writes) {
      const ref = fsDoc2(db, write.path);
      if (write.kind === "set") batch.set(ref, write.data);
      else if (write.kind === "delete") batch.delete(ref);
      else batch.update(ref, write.updates);
    }
    const writePromise = batch.commit();
    if (!isOnline()) {
      writePromise.catch((err) => logOfflineWrite(`commitBatch ${writes.length} writes`, err));
      return;
    }
    await writePromise;
  },
  async addDoc(collectionPath, data) {
    const ref = fsDoc2(fsCollection2(db, collectionPath));
    const writePromise = setDoc(ref, data);
    if (!isOnline()) {
      writePromise.catch((err) => logOfflineWrite(`addDoc ${collectionPath}`, err));
      return ref.id;
    }
    await writePromise;
    return ref.id;
  },
  async deleteDoc(path) {
    const writePromise = fsDeleteDoc(fsDoc2(db, path));
    if (!isOnline()) {
      writePromise.catch((err) => logOfflineWrite(`deleteDoc ${path}`, err));
      return;
    }
    await writePromise;
  },
  deleteFieldValue: () => deleteField(),
  arrayUnionValue: (...values) => arrayUnion(...values),
  arrayRemoveValue: (...values) => arrayRemove(...values)
};

// src/dao/AppDao.ts
import { AppEquipmentConfigSchema, AppTaskScreenConfigSchema } from "@rock/types";
import { BaseDao, appEquipmentConfigPath, appTaskScreenConfigPath } from "@rock/dao";
import { setDoc as setDoc2, updateDoc as updateDoc2 } from "firebase/firestore";

// src/typedRef.ts
import { collection as collection2, collectionGroup, doc } from "firebase/firestore";
function typedDoc(path) {
  return doc(db, path);
}
function typedCollection(path) {
  return collection2(db, path);
}
function typedCollectionGroup(name) {
  return collectionGroup(db, name);
}

// src/dao/AppDao.ts
var AppDao = class extends BaseDao {
  constructor() {
    super("AppDao");
  }
  /**
   * Gets an atom that watches the app equipment config.
   */
  appEquipmentConfigAtom() {
    return atomWithDocument(typedDoc(appEquipmentConfigPath()), {
      schema: AppEquipmentConfigSchema,
      log: this.log
    });
  }
  /**
   * Gets an atom that watches the app task screen config.
   */
  appTaskScreenConfigAtom() {
    return atomWithDocument(typedDoc(appTaskScreenConfigPath()), {
      schema: AppTaskScreenConfigSchema,
      log: this.log
    });
  }
  /**
   * Replace the core-field list — the fields every equipment form is built from.
   *
   * Field-level, so the write can only touch `coreFields`. Firestore can't patch one entry of an
   * array, so the caller hands over the whole list and it lands as given: pass a list derived from
   * the PARSED config and schema defaults (e.g. `defaultLengthSelection`, `enabled`) materialize
   * into the stored entries, and any key the schema doesn't declare is dropped.
   */
  async setCoreFields(coreFields) {
    const ref = typedDoc(appEquipmentConfigPath());
    this.log.info("Setting AppEquipmentConfig core fields", { path: ref.path, count: coreFields.length });
    await updateDoc2(ref, { coreFields });
  }
  /**
   * Save the app-level default task screen config.
   */
  async setTaskScreenConfig(config) {
    const ref = typedDoc(appTaskScreenConfigPath());
    this.log.info("Setting AppTaskScreenConfig", { path: ref.path, config });
    await setDoc2(ref, config);
  }
};

// src/dao/AppFlowDao.ts
import { BaseDao as BaseDao2, draftFlowPath, draftFlowsCollection, spaceFlowPath } from "@rock/dao";
import { deleteDoc, setDoc as setDoc3 } from "firebase/firestore";
import * as z from "zod";
var AppFlowDao = class extends BaseDao2 {
  constructor() {
    super("AppFlowDao");
  }
  /**
   * Gets an atom that watches all draft flows for a space.
   */
  draftFlowsAtom(spaceId) {
    return atomWithQuery(
      async (get) => {
        const spaceIdValue = await unwrapValue(get, spaceId);
        if (spaceIdValue) {
          return typedCollection(draftFlowsCollection(spaceIdValue));
        }
      },
      {
        schema: z.custom(),
        log: this.log
      }
    );
  }
  /**
   * Gets an atom that watches a single draft flow.
   */
  draftFlowAtom(spaceId, flowId) {
    return atomWithDocument(
      async (get) => {
        const { spaceIdValue, flowIdValue } = await unwrapAll(get, {
          spaceIdValue: spaceId,
          flowIdValue: flowId
        });
        if (spaceIdValue && flowIdValue) {
          return typedDoc(draftFlowPath(spaceIdValue, flowIdValue));
        }
      },
      {
        useTransition: true,
        schema: z.custom(),
        log: this.log
      }
    );
  }
  /**
   * Gets an atom that watches the live/published flow for a space and equipment type.
   */
  liveFlowAtom(params) {
    return atomWithDocument(typedDoc(spaceFlowPath(params.spaceId, params.equipmentTypeId)), {
      schema: z.custom(),
      log: this.log
    });
  }
  /**
   * Set (create or replace) a draft flow.
   */
  async setFlow(spaceId, flowId, data) {
    const ref = typedDoc(draftFlowPath(spaceId, flowId));
    this.log.info("Setting draft flow", { path: ref.path, data });
    await setDoc3(ref, data);
  }
  /**
   * Set (create or replace) a published flow.
   */
  async setPublishedFlow(spaceId, equipmentTypeId, data) {
    const ref = typedDoc(spaceFlowPath(spaceId, equipmentTypeId));
    this.log.info("Setting published flow", { path: ref.path, data });
    await setDoc3(ref, data);
  }
  /**
   * Delete a published flow.
   */
  async deletePublishedFlow(spaceId, equipmentTypeId) {
    const ref = typedDoc(spaceFlowPath(spaceId, equipmentTypeId));
    this.log.info("Deleting published flow", { path: ref.path });
    await deleteDoc(ref);
  }
  /**
   * Updates a draft flow with partial data.
   * Note: This method requires a Jotai Getter function to resolve ValueOrAtom parameters.
   */
  async updateFlow(get, spaceId, flowId, updates) {
    const spaceIdValue = await unwrapValue(get, spaceId);
    const flowIdValue = await unwrapValue(get, flowId);
    if (spaceIdValue && flowIdValue) {
      const ref = typedDoc(draftFlowPath(spaceIdValue, flowIdValue));
      await setDoc3(ref, updates, { merge: true });
    }
  }
  /**
   * Delete a draft flow.
   */
  async deleteDraftFlow(spaceId, flowId) {
    const ref = typedDoc(draftFlowPath(spaceId, flowId));
    this.log.info("Deleting draft flow", { path: ref.path });
    await deleteDoc(ref);
  }
};

// src/dao/AppliesToDraftResultDao.ts
import { AppliesToDraftResultSchema } from "@rock/types";
import { BaseAppliesToDraftResultDao, appliesToDraftResultsCollection } from "@rock/dao";
import { orderBy, query, where } from "firebase/firestore";
var AppliesToDraftResultDao = class extends BaseAppliesToDraftResultDao {
  /** Watches a consideration type's applies-to drafts, newest first. */
  appliesToDraftResultsForConsiderationTypeAtom(considerationTypeId) {
    return atomWithQuery(
      async (get) => {
        const typeIdValue = await unwrapValue(get, considerationTypeId);
        if (typeIdValue) {
          return query(
            typedCollection(appliesToDraftResultsCollection()),
            where("considerationTypeId", "==", typeIdValue),
            orderBy("createdAt", "desc")
          );
        }
      },
      { schema: AppliesToDraftResultSchema, log: this.log }
    );
  }
};

// src/dao/AppStatusDao.ts
import { APP_STATUS_DOC_SCHEMAS } from "@rock/types";
import { BaseAppStatusDao, appStatusDocPath, computeDraftStatus, toDraftPath } from "@rock/dao";
import { atom as atom3 } from "jotai";
var AppStatusDao = class extends BaseAppStatusDao {
  // Inherits draft/publish methods (setAppStatusDraft, publishAppStatus, ...) from BaseAppStatusDao
  /**
   * Gets an atom that watches one live `app-status/{kind}` doc.
   */
  appStatusDocAtom(kind) {
    return atomWithDocument(typedDoc(appStatusDocPath(kind)), {
      schema: APP_STATUS_DOC_SCHEMAS[kind],
      log: this.log
    });
  }
  /**
   * Watches one staged app-status *draft* (admin working copy in the `_draft/db` mirror).
   * Read-only by convention: stage edits through `setAppStatusDraft()` (the DraftLayer choke
   * point that stamps `_draft: true`), never by setting this atom.
   */
  appStatusDraftAtom(kind) {
    return atomWithDocument(typedDoc(toDraftPath(appStatusDocPath(kind))), {
      schema: APP_STATUS_DOC_SCHEMAS[kind],
      log: this.log
    });
  }
  /**
   * Reactive draft/publish status for one tool, derived from its draft and live docs. Drives the
   * "unpublished changes" badge and Publish gating. Ignores `updatedAt` like publish does, so the
   * restamped timestamp never reads as a pending change.
   */
  appStatusDraftStatusAtom(kind) {
    const draftAtom = this.appStatusDraftAtom(kind);
    const liveAtom = this.appStatusDocAtom(kind);
    return atom3(async (get) => {
      const [draft, live] = await Promise.all([get(draftAtom), get(liveAtom)]);
      return computeDraftStatus(draft, live, ["updatedAt"]);
    });
  }
};

// src/dao/BrandAliasDao.ts
import { BaseBrandAliasDao, brandAliasesCollection, brandAliasPath } from "@rock/dao";
import { BrandAliasSchema } from "@rock/types";
import { getDocs as getDocs2, query as query2, where as where2 } from "firebase/firestore";
var BrandAliasDao = class extends BaseBrandAliasDao {
  // Inherits: getBrandAlias(), setBrandAlias(), updateBrandAlias()
  /** One-shot: the alias docs a brand owns — what an alias removal may delete. */
  async findAliasesByBrandId(brandId) {
    const snap = await getDocs2(
      query2(typedCollection(brandAliasesCollection()), where2("brandId", "==", brandId))
    );
    return snap.docs.map((doc3) => parseWithSchema(doc3.data(), BrandAliasSchema, doc3.ref.path, this.log)).filter((alias) => !!alias);
  }
  /** One alias projection by its folded key, live — what a name resolves to (a brand, a line, a denial, or nothing). */
  brandAliasAtom(aliasKey) {
    return atomWithDocument(
      async (get) => {
        const key = await unwrapValue(get, aliasKey);
        if (key) return typedDoc(brandAliasPath(key));
      },
      { schema: BrandAliasSchema, log: this.log }
    );
  }
  /** Every alias projection pointing at a brand, live — the resolvable keys on the brand's Aliases tab. */
  aliasesByBrandAtom(brandId) {
    return atomWithQuery(
      async (get) => {
        const brandIdValue = await unwrapValue(get, brandId);
        if (brandIdValue) {
          return query2(
            typedCollection(brandAliasesCollection()),
            where2("brandId", "==", brandIdValue)
          );
        }
      },
      { schema: BrandAliasSchema, log: this.log }
    );
  }
};

// src/dao/BrandDao.ts
import { BrandAliasKeySchema, BrandReviewStatusSchema, BrandSchema } from "@rock/types";
import { BaseBrandDao, brandAliasPath as brandAliasPath2, brandPath, brandsCollection } from "@rock/dao";
import { normalizeBrandName } from "@rock/util";
import {
  deleteField as deleteField2,
  getCountFromServer,
  getDoc as getDoc3,
  limit,
  orderBy as orderBy3,
  query as query3,
  updateDoc as updateDoc3,
  where as where3
} from "firebase/firestore";

// src/querySort.ts
import { orderBy as orderBy2 } from "firebase/firestore";
function sortConstraint(sort, defaultField, defaultDir = "asc") {
  return orderBy2(sort?.field ?? defaultField, sort?.dir ?? defaultDir);
}

// src/dao/BrandDao.ts
var LIVE_BRAND_REVIEW_STATUSES = BrandReviewStatusSchema.options.filter((status) => status !== "junk");
var BrandDao = class extends BaseBrandDao {
  // Inherits: getBrand(), setBrand(), updateBrand() from BaseBrandDao
  /**
   * Gets an atom that watches brands, sorted by canonicalName, with an optional query limit, an
   * optional server-side `reviewStatus` filter (`'all'`/omitted runs unfiltered — the catalog-wide
   * joins elsewhere rely on seeing every brand regardless of status), and an optional `namePrefix`
   * "starts with" match on `canonicalKey`. The prefix must already be folded into key space
   * (`normalizeBrandName(raw).key`: accents and separators gone), so `KUHL` finds `KÜHL`.
   *
   * A prefix search PINS the ordering to `canonicalKey` (Firestore requires the range field to lead),
   * so only the name column's direction survives `sort` — see `ModelDao.modelsAtom`.
   */
  brandsAtom(queryLimit, reviewStatusFilter, namePrefix, sort) {
    return atomWithQuery(
      async (get) => {
        const { sortValue, statusFilter, prefix, lim } = await unwrapAll(get, {
          sortValue: sort,
          statusFilter: reviewStatusFilter,
          prefix: namePrefix,
          lim: queryLimit
        });
        const constraints = [];
        if (statusFilter === "live") {
          constraints.push(where3("reviewStatus", "in", LIVE_BRAND_REVIEW_STATUSES));
        } else if (statusFilter && statusFilter !== "all") {
          constraints.push(where3("reviewStatus", "==", statusFilter));
        }
        if (prefix) {
          const dir = sortValue?.field === "canonicalName" ? sortValue.dir : "asc";
          constraints.push(orderBy3("canonicalKey", dir));
          constraints.push(where3("canonicalKey", ">=", prefix));
          constraints.push(where3("canonicalKey", "<=", `${prefix}\uF8FF`));
        } else {
          constraints.push(sortConstraint(sortValue, "canonicalName"));
        }
        if (lim) {
          constraints.push(limit(lim));
        }
        return query3(typedCollection(brandsCollection()), ...constraints);
      },
      { schema: BrandSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches a single brand.
   */
  brandAtom(brandId) {
    return atomWithDocument(
      async (get) => {
        const brandIdValue = await unwrapValue(get, brandId);
        if (brandIdValue) {
          return typedDoc(brandPath(brandIdValue));
        }
      },
      { schema: BrandSchema, log: this.log }
    );
  }
  /** Total brand docs in the catalog, junk included (server aggregation, one-shot). */
  async countBrands() {
    const snapshot = await getCountFromServer(typedCollection(brandsCollection()));
    return snapshot.data().count;
  }
  /**
   * Count of brands carrying one curation status — the dashboard's review breakdown. Single-field
   * equality, so the automatic index serves it, and it counts exactly what the list's matching status
   * filter shows. Brands minted before `reviewStatus` existed were backfilled to an explicit value
   * (the schema default only covers the read path; an absent field matches no equality query).
   */
  async countBrandsWithReviewStatus(reviewStatus) {
    const snapshot = await getCountFromServer(
      query3(typedCollection(brandsCollection()), where3("reviewStatus", "==", reviewStatus))
    );
    return snapshot.data().count;
  }
  /**
   * Admin edit of the brand's display fields. `canonicalName` is folded like every other writer (the
   * whole catalog stores the uppercase fold — free-form casing would drift from it) and is display-only:
   * the canonical KEY never changes here. Empty website/description clear the field — the
   * `deleteField()` stays in the DAO.
   *
   * A CHANGED `canonicalName` must still fold to an identity THIS brand answers to. `canonicalKey` is
   * minted once and never updated — it is the doc id of a `brand-aliases/{key}` projection, which is how
   * every name resolves to a brand, while the app's typeahead recomputes its match key from the
   * `canonicalName` the search index ships. Let the name fold to a key that resolves elsewhere (or
   * nowhere) and matching and resolution answer to different brands.
   *
   * Folding to something OTHER than `canonicalKey` is legitimate when the brand already owns that key:
   * prod's GE carries `canonicalKey` GE with `canonicalName` "GENERAL ELECTRIC", because a curator
   * registered the formal name as an alias first, so `brand-aliases/GENERALELECTRIC` resolves to it too.
   * That is the curation workflow for any brand whose formal name is longer than its minted key. So the
   * test is the alias DOC, the same one `resolveBrandRef` reads — not `stored.aliases`, a display list
   * that predates alias projections on older brands and can name a key nothing resolves.
   *
   * Only a CHANGE is judged. A brand whose stored name already drifts (the field was free-text before
   * this guard) has to stay editable in its other fields rather than being frozen by an invariant it
   * already violates.
   *
   * Read with `getDoc`, not `getBrand`: that returns undefined for a doc failing `BrandSchema` as well as
   * for one that doesn't exist, and skipping the check on the legacy docs most likely to be under repair
   * is the one outcome worse than refusing the edit.
   */
  async updateBrandDetails(brandId, updates) {
    const ref = typedDoc(brandPath(brandId));
    const stored = (await getDoc3(ref)).data();
    if (!stored) throw new Error(`No brand to update at ${ref.path}`);
    const { display, key } = normalizeBrandName(updates.canonicalName);
    if (updates.canonicalName.trim() && !key) {
      throw new Error(`Canonical name "${updates.canonicalName}" folds to nothing \u2014 it keys on no name.`);
    }
    const isRename = !!display && display !== stored.canonicalName;
    if (isRename && !await this.resolvesToBrand(key, brandId, stored.canonicalKey)) {
      this.log.warn("Refused a brand rename to a name that resolves elsewhere", {
        path: ref.path,
        from: stored.canonicalName,
        to: display,
        canonicalKey: stored.canonicalKey
      });
      throw new Error(
        `Canonical name "${display}" keys to ${key}, which is neither this brand's identity key ${stored.canonicalKey} nor one of its aliases \u2014 the name would resolve to a different brand, or to none. Add it as an alias first, then it can become the canonical name.`
      );
    }
    this.log.info("Updating brand details", { path: ref.path });
    await updateDoc3(ref, {
      canonicalName: isRename ? display : stored.canonicalName,
      displayName: updates.displayName || deleteField2(),
      website: updates.website || deleteField2(),
      description: updates.description || deleteField2(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  async updateBrandDisplayName(brandId, displayName) {
    const ref = typedDoc(brandPath(brandId));
    this.log.info("Updating brand display name", { path: ref.path });
    await updateDoc3(ref, { displayName: displayName || deleteField2(), updatedAt: (/* @__PURE__ */ new Date()).toISOString() });
  }
  /**
   * Does `key` resolve to this brand — its own identity key, or an alias projection pointing back at it?
   * The alias read mirrors `resolveBrandRef`, so the guard above is exactly as strict as resolution and no
   * stricter. A deny placeholder (a shared nameplate like bare MAYTAG) carries no `brandId` and so fails,
   * which is correct: such a name must never become any brand's canonical name.
   */
  async resolvesToBrand(key, brandId, canonicalKey) {
    if (key === canonicalKey) return true;
    const aliasRef = typedDoc(brandAliasPath2(BrandAliasKeySchema.parse(key)));
    return (await getDoc3(aliasRef)).data()?.brandId === brandId;
  }
  /**
   * Replace the brand's explicit `domains` list (callers compute the next list with the pure
   * `withBrandDomainAdded`/`withBrandDomainRemoved` helpers in `@rock/brands`). An empty list clears
   * the field — the `deleteField()` stays in the DAO.
   */
  async setBrandDomains(brandId, domains) {
    const ref = typedDoc(brandPath(brandId));
    this.log.info("Setting brand domains", { path: ref.path, count: domains.length });
    await updateDoc3(ref, {
      domains: domains.length ? domains : deleteField2(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  /**
   * Replace the brand's AI-researched `researchedDomains` list (callers compute the next list with the
   * pure `withResearchedDomainsAdded`/`withResearchedDomainRemoved` helpers in `@rock/brands`). An empty
   * list clears the field.
   */
  async setBrandResearchedDomains(brandId, researchedDomains) {
    const ref = typedDoc(brandPath(brandId));
    this.log.info("Setting brand researched domains", { path: ref.path, count: researchedDomains.length });
    await updateDoc3(ref, {
      researchedDomains: researchedDomains.length ? researchedDomains : deleteField2(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  /**
   * Replace the brand's `trustedDomains` list (callers compute the next list with the pure
   * `withTrustedDomainAdded`/`withTrustedDomainRemoved` helpers in `@rock/brands`). An empty list clears
   * the field.
   */
  async setBrandTrustedDomains(brandId, trustedDomains) {
    const ref = typedDoc(brandPath(brandId));
    this.log.info("Setting brand trusted domains", { path: ref.path, count: trustedDomains.length });
    await updateDoc3(ref, {
      trustedDomains: trustedDomains.length ? trustedDomains : deleteField2(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  /**
   * Admin assignment of one of the brand's asset slots (`logo` / `logoOnDark` / `icon`). Stamps
   * `pinned: true` — a human chose this asset, so automated enrichment must never overwrite it (the
   * enrichment apply path skips pinned slots). Passing `null` clears the slot (the `deleteField()`
   * stays in the DAO).
   *
   * `source` is PRESERVED, never stamped: it records where the bytes came from (Brandfetch, the web
   * image search, an admin upload), which is a different question from who put them here. Stamping it
   * on assignment is what made every admin pick claim to be `'manual'`, erasing the origin the card
   * now shows.
   *
   * `updateDoc`, not `setDoc(..., { merge: true })`: a merged write deep-merges the slot MAP, so
   * fields the new asset doesn't carry (`svgUrl`, `transparent`, …) survived from whatever asset
   * previously sat in the slot — a web pick would inherit the outgoing Brandfetch asset's vector URL
   * and background flag. The slot is replaced wholesale.
   */
  async setBrandAsset(brandId, slot, asset) {
    const ref = typedDoc(brandPath(brandId));
    this.log.info("Setting brand asset", { path: ref.path, slot, cleared: !asset });
    await updateDoc3(ref, {
      [slot]: asset ? { ...asset, pinned: true } : deleteField2(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
};

// src/dao/BrandRequestDao.ts
import { BaseBrandRequestDao, brandRequestPath, brandRequestsCollection } from "@rock/dao";
import { BrandRequestSchema } from "@rock/types";
import { getCountFromServer as getCountFromServer2, limit as limit2, orderBy as orderBy4, query as query4, where as where4 } from "firebase/firestore";
var BrandRequestDao = class extends BaseBrandRequestDao {
  // Inherits: getBrandRequest(), setBrandRequest(), updateBrandRequest(), resolveBrandRequest()
  /**
   * The queue in one status, most recently seen first. Pending is the work list; parked is set aside
   * until seen again; resolved is the history. Impact (sighting counts, spaces) is a column, not the sort: a name seen today is what an
   * admin is most likely to be asked about.
   */
  requestsAtom(status, queryLimit) {
    return atomWithQuery(
      async (get) => {
        const { statusValue, lim } = await unwrapAll(get, { statusValue: status, lim: queryLimit });
        if (!statusValue) return void 0;
        const constraints = [
          where4("status", "==", statusValue),
          orderBy4("lastSeenAt", "desc")
        ];
        if (lim) constraints.push(limit2(lim));
        return query4(typedCollection(brandRequestsCollection()), ...constraints);
      },
      { schema: BrandRequestSchema, log: this.log }
    );
  }
  requestAtom(requestId) {
    return atomWithDocument(
      async (get) => {
        const id = await unwrapValue(get, requestId);
        if (id) return typedDoc(brandRequestPath(id));
      },
      { schema: BrandRequestSchema, log: this.log }
    );
  }
  /** How many names are waiting on a human — the nav badge (server aggregation, one-shot). */
  async countPending() {
    const snapshot = await getCountFromServer2(
      query4(typedCollection(brandRequestsCollection()), where4("status", "==", "pending"))
    );
    return snapshot.data().count;
  }
};

// src/dao/BrandResearchDao.ts
import { BaseBrandResearchDao, brandResearchCollection } from "@rock/dao";
import { BrandResearchSchema } from "@rock/types";
import { limit as limit3, orderBy as orderBy5, query as query5 } from "firebase/firestore";
var BrandResearchDao = class extends BaseBrandResearchDao {
  // Inherits: getBrandResearch(), setBrandResearch()
  /** A request's research runs, newest first: the latest is the one the request page offers. */
  researchAtom(requestId, queryLimit = 5) {
    return atomWithQuery(
      async (get) => {
        const id = await unwrapValue(get, requestId);
        if (!id) return void 0;
        return query5(
          typedCollection(brandResearchCollection(id)),
          orderBy5("requestedAt", "desc"),
          limit3(queryLimit)
        );
      },
      { schema: BrandResearchSchema, log: this.log }
    );
  }
};

// src/dao/BrandSearchIndexDao.ts
import { BaseBrandSearchIndexDao } from "@rock/dao";
var BrandSearchIndexDao = class extends BaseBrandSearchIndexDao {
  // Inherits: getBrandSearchIndex(). Read once per page load; the scheduled rebuild is its only writer.
};

// src/dao/BrandSourceDao.ts
import { BaseBrandSourceDao, brandSourcePath } from "@rock/dao";
import { BrandSourceSchema } from "@rock/types";
var BrandSourceDao = class extends BaseBrandSourceDao {
  // Inherits: getBrandSource(), setBrandSource()
  /**
   * Gets an atom that watches a brand's stored source snapshot (admin-only in rules) — the asset
   * browser renders every candidate from this, with zero Brandfetch API calls.
   */
  brandSourceAtom(brandId, sourceId) {
    return atomWithDocument(
      async (get) => {
        const brandIdValue = await unwrapValue(get, brandId);
        if (brandIdValue) {
          return typedDoc(brandSourcePath(brandIdValue, sourceId));
        }
      },
      { schema: BrandSourceSchema, log: this.log }
    );
  }
};

// src/dao/ChatConversationDao.ts
import { BaseChatConversationDao, chatConversationPath, chatConversationsCollection } from "@rock/dao";
import { ChatConversationSchema } from "@rock/types";
import { limit as limit4, orderBy as orderBy6, query as query6 } from "firebase/firestore";
var ChatConversationDao = class extends BaseChatConversationDao {
  // Inherits: getConversation(), setConversation(), updateConversation() from BaseChatConversationDao
  /**
   * Watches a user's most recent conversations, newest activity first (single-field orderBy —
   * auto-indexed).
   */
  conversationsAtom(userId, count = 50) {
    return atomWithQuery(
      async (get) => {
        const userIdValue = await unwrapValue(get, userId);
        if (userIdValue) {
          return query6(
            typedCollection(chatConversationsCollection(userIdValue)),
            orderBy6("updatedAt", "desc"),
            limit4(count)
          );
        }
      },
      { schema: ChatConversationSchema, log: this.log }
    );
  }
  /** Watches a single conversation (messages are embedded — this is the whole transcript). */
  conversationAtom(params) {
    return atomWithDocument(
      async (get) => {
        const value = await unwrapValue(get, params);
        if (value) {
          return typedDoc(chatConversationPath(value.userId, value.conversationId));
        }
      },
      { schema: ChatConversationSchema, log: this.log }
    );
  }
};

// src/dao/CompanyDao.ts
import { BaseCompanyDao, companiesCollection, companyPath } from "@rock/dao";
import { CompanySchema } from "@rock/types";
import { limit as limit5, query as query7, setDoc as setDoc4 } from "firebase/firestore";
var CompanyDao = class extends BaseCompanyDao {
  // Inherits: getCompany(), setCompany(), updateCompany(), deleteCompany()
  /**
   * Atom that watches all companies, sorted by name (or by the caller's column `sort`). Used for the
   * admin catalog. No filters, so every ordering rides an automatic single-field index.
   */
  companiesAtom(queryLimit, sort) {
    return atomWithQuery(
      async (get) => {
        const { sortValue, lim } = await unwrapAll(get, { sortValue: sort, lim: queryLimit });
        const constraints = [sortConstraint(sortValue, "name")];
        if (lim) {
          constraints.push(limit5(lim));
        }
        return query7(typedCollection(companiesCollection()), ...constraints);
      },
      { schema: CompanySchema, log: this.log }
    );
  }
  /**
   * Atom that watches a single company.
   */
  companyAtom(companyId) {
    return atomWithDocument(
      async (get) => {
        const idValue = await unwrapValue(get, companyId);
        if (idValue) {
          return typedDoc(companyPath(idValue));
        }
      },
      { schema: CompanySchema, log: this.log }
    );
  }
  /**
   * Overrides the logo URL for a company. Takes precedence over the Brandfetch-fetched logo.
   */
  async setLogoOverride(companyId, logoOverrideUrl) {
    const ref = typedDoc(companyPath(companyId));
    this.log.info("Setting company logo override", { path: ref.path });
    await setDoc4(ref, { logoOverrideUrl }, { merge: true });
  }
};

// src/dao/ConsiderationDao.ts
import { ConsiderationSchema } from "@rock/types";
import { BaseConsiderationDao, considerationsCollection, considerationsCollectionGroupName } from "@rock/dao";
import { orderBy as orderBy7, query as query8, where as where5 } from "firebase/firestore";
var ConsiderationDao = class extends BaseConsiderationDao {
  // Inherits: getConsideration(), setConsideration(), updateConsideration() from BaseConsiderationDao
  /**
   * Gets an atom that watches all considerations for a specific consideration type across all homes.
   */
  considerationsForConsiderationTypeAtom(considerationTypeId) {
    return atomWithQuery(
      async (get) => {
        const typeIdValue = await unwrapValue(get, considerationTypeId);
        if (typeIdValue) {
          return query8(
            typedCollectionGroup(considerationsCollectionGroupName()),
            where5("typeId", "==", typeIdValue),
            orderBy7("updatedAt", "desc")
          );
        }
      },
      { schema: ConsiderationSchema, log: this.log }
    );
  }
  /**
   * Watches all considerations for ONE home (a single home-level subscription). The per-home
   * considerations tab groups these by typeId client-side — one listener instead of one
   * cross-home collection-group listener per consideration-type row.
   */
  considerationsForHomeAtom(homeId) {
    return atomWithQuery(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return query8(typedCollection(considerationsCollection(homeIdValue)));
        }
      },
      { schema: ConsiderationSchema, log: this.log }
    );
  }
};

// src/dao/ConsiderationTypeDao.ts
import { ConsiderationTypeSchema } from "@rock/types";
import { BaseConsiderationTypeDao, considerationTypePath, considerationTypesCollection } from "@rock/dao";
import { query as query9, where as where6 } from "firebase/firestore";
var ConsiderationTypeDao = class extends BaseConsiderationTypeDao {
  // Inherits: getConsiderationType(), setConsiderationType(), updateConsiderationType() from BaseConsiderationTypeDao
  /**
   * Gets an atom that watches all consideration types for a space.
   */
  considerationTypesForSpaceAtom(spaceId) {
    return atomWithQuery(
      async (get) => {
        const spaceIdValue = await unwrapValue(get, spaceId);
        if (spaceIdValue) {
          return query9(
            typedCollection(considerationTypesCollection()),
            where6("spaceId", "==", spaceIdValue)
          );
        }
      },
      {
        schema: ConsiderationTypeSchema,
        log: this.log
      }
    );
  }
  /**
   * Gets an atom that watches all consideration types.
   */
  considerationTypesAtom() {
    return atomWithQuery(typedCollection(considerationTypesCollection()), {
      schema: ConsiderationTypeSchema,
      log: this.log
    });
  }
  /**
   * Gets an atom that watches a single consideration type.
   */
  considerationTypeAtom(typeId) {
    return atomWithDocument(
      async (get) => {
        const typeIdValue = await unwrapValue(get, typeId);
        if (typeIdValue) {
          return typedDoc(considerationTypePath(typeIdValue));
        }
      },
      { schema: ConsiderationTypeSchema, log: this.log }
    );
  }
};

// src/dao/CustomEquipmentTypeDao.ts
import {
  CustomEquipmentTypeSchema
} from "@rock/types";
import {
  BaseCustomEquipmentTypeDao,
  customEquipmentTypePath,
  customEquipmentTypesCollection,
  customEquipmentTypesCollectionGroupName
} from "@rock/dao";
import { deleteDoc as deleteDoc2, getDocs as getDocs3, query as query10, where as where7 } from "firebase/firestore";
var CustomEquipmentTypeDao = class extends BaseCustomEquipmentTypeDao {
  /**
   * Watches all custom equipment types for a home.
   */
  customEquipmentTypesAtom(homeId) {
    return atomWithQuery(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return typedCollection(customEquipmentTypesCollection(homeIdValue));
        }
      },
      {
        schema: CustomEquipmentTypeSchema,
        log: this.log
      }
    );
  }
  /**
   * Watches a single custom equipment type.
   */
  customEquipmentTypeAtom(homeId, typeId) {
    return atomWithDocument(
      async (get) => {
        const { homeIdValue, typeIdValue } = await unwrapAll(get, {
          homeIdValue: homeId,
          typeIdValue: typeId
        });
        if (homeIdValue && typeIdValue) {
          return typedDoc(customEquipmentTypePath(homeIdValue, typeIdValue));
        }
      },
      { schema: CustomEquipmentTypeSchema, log: this.log }
    );
  }
  /**
   * Fetches every custom (across all homes) linked to a candidate. Powers the
   * candidate detail page in rock-admin. One-shot read, not a live subscription.
   */
  async listCustomsByCandidateId(candidateId) {
    const snapshot = await getDocs3(
      query10(
        typedCollectionGroup(customEquipmentTypesCollectionGroupName()),
        where7("candidateId", "==", candidateId)
      )
    );
    const customs = [];
    for (const doc3 of snapshot.docs) {
      const parsed = parseWithSchema(doc3.data(), CustomEquipmentTypeSchema, doc3.ref.path, this.log);
      if (parsed) {
        customs.push(parsed);
      }
    }
    return customs;
  }
  /**
   * Delete a CustomEquipmentType.
   */
  async delete(homeId, typeId) {
    const ref = typedDoc(customEquipmentTypePath(homeId, typeId));
    this.log.info("Deleting CustomEquipmentType", { path: ref.path });
    await deleteDoc2(ref);
  }
};

// src/dao/DeviceDao.ts
import { BaseDeviceDao, userDevicesCollection } from "@rock/dao";
import { DeviceSchema } from "@rock/types";
import { limit as limit6, orderBy as orderBy8, query as query11 } from "firebase/firestore";
var DeviceDao = class extends BaseDeviceDao {
  // Inherits: getDevice(), setDevice(), updateDevice() from BaseDeviceDao
  /** Watches a user's devices, most recently active first (capped — devices accumulate across reinstalls). */
  devicesAtom(userId, count = 50) {
    return atomWithQuery(
      async (get) => {
        const userIdValue = await unwrapValue(get, userId);
        if (userIdValue) {
          return query11(
            typedCollection(userDevicesCollection(userIdValue)),
            orderBy8("updatedAt", "desc"),
            limit6(count)
          );
        }
      },
      { schema: DeviceSchema, log: this.log }
    );
  }
};

// src/dao/DocumentSearchDao.ts
import { BaseDocumentSearchDao, documentSearchesCollection } from "@rock/dao";
import { DocumentSearchSchema } from "@rock/types";
import { doc as doc2, limit as limit7, orderBy as orderBy9, query as query12 } from "firebase/firestore";
var DocumentSearchDao = class extends BaseDocumentSearchDao {
  // Inherits: getDocumentSearch(), setDocumentSearch() — setDocumentSearch is the create.
  /** A fresh auto-generated search id (web SDK id generator; no doc is created). */
  newSearchId(modelId) {
    return doc2(typedCollection(documentSearchesCollection(modelId))).id;
  }
  /** Atom watching a model's searches, newest request first (the first entry is the active/latest one). */
  searchesAtom(modelId, count = 5) {
    return atomWithQuery(
      async (get) => {
        const modelIdValue = await unwrapValue(get, modelId);
        if (modelIdValue) {
          return query12(
            typedCollection(documentSearchesCollection(modelIdValue)),
            orderBy9("requestedAt", "desc"),
            limit7(count)
          );
        }
      },
      { schema: DocumentSearchSchema, log: this.log }
    );
  }
};

// src/dao/EquipmentDao.ts
import { EquipmentSchema } from "@rock/types";
import { BaseEquipmentDao, equipmentCollection, equipmentCollectionGroupName } from "@rock/dao";
import { getCountFromServer as getCountFromServer3, limit as limit8, query as query13, where as where8 } from "firebase/firestore";
var EquipmentDao = class extends BaseEquipmentDao {
  // Inherits: getEquipment(), setEquipment(), updateEquipment() from BaseEquipmentDao
  /**
   * Gets an atom that watches all active equipment for a home (one home-level collection).
   */
  equipmentListAtom(homeId) {
    return atomWithQuery(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return query13(
            typedCollection(equipmentCollection(homeIdValue)),
            where8("active", "==", true)
          );
        }
      },
      { schema: EquipmentSchema, log: this.log }
    );
  }
  /**
   * Watches up to `max` equipment instances resolved to a given model, across every home — the
   * admin model page's Equipment tab. Backed by the `details.modelId` collection-group index.
   * `active` isn't filtered here (that would need a composite index); callers filter it client-side.
   */
  equipmentForModelAtom(modelId, max = 10) {
    return atomWithQuery(
      async (get) => {
        const modelIdValue = await unwrapValue(get, modelId);
        if (modelIdValue) {
          return query13(
            typedCollectionGroup(equipmentCollectionGroupName()),
            where8("details.modelId", "==", modelIdValue),
            limit8(max)
          );
        }
      },
      { schema: EquipmentSchema, log: this.log }
    );
  }
  /**
   * Total count of equipment docs across every home (unfiltered collection-group aggregation —
   * needs no index). One-shot, not a live subscription.
   */
  async countAllEquipment() {
    const snapshot = await getCountFromServer3(typedCollectionGroup(equipmentCollectionGroupName()));
    return snapshot.data().count;
  }
  /**
   * Count of equipment docs in one space across every home — a collection-group aggregation on the
   * `spaceId` field, served by its single-field COLLECTION_GROUP exemption in firestore.indexes.json
   * (a composite's prefix does NOT serve a single-field CG filter — learned in prod).
   * One-shot, not a live subscription.
   */
  async countEquipmentForSpace(spaceId) {
    const snapshot = await getCountFromServer3(
      query13(typedCollectionGroup(equipmentCollectionGroupName()), where8("spaceId", "==", spaceId))
    );
    return snapshot.data().count;
  }
  /**
   * True total count of equipment resolved to a model, across every home — a server-side aggregation
   * (one billed read, no documents transferred) so the model page's tab can show an honest total
   * even though the list itself is capped. One-shot, not a live subscription.
   */
  async countEquipmentForModel(modelId) {
    const snapshot = await getCountFromServer3(
      query13(
        typedCollectionGroup(equipmentCollectionGroupName()),
        where8("details.modelId", "==", modelId)
      )
    );
    return snapshot.data().count;
  }
};

// src/dao/EquipmentRefreshDao.ts
import { BaseEquipmentRefreshDao } from "@rock/dao";
var EquipmentRefreshDao = class extends BaseEquipmentRefreshDao {
  // Inherits: getEquipmentRefresh(), setEquipmentRefresh(), updateEquipmentRefresh(), claimEquipmentRefresh() from BaseEquipmentRefreshDao
};

// src/dao/EquipmentTestLibraryDao.ts
import { BaseEquipmentTestLibraryDao, equipmentTestLibraryCollection, equipmentTestLibraryEntryPath } from "@rock/dao";
import {
  EquipmentTestLibraryEntrySchema
} from "@rock/types";
import { orderBy as orderBy10, query as query14 } from "firebase/firestore";
var EquipmentTestLibraryDao = class extends BaseEquipmentTestLibraryDao {
  // Inherits: getEntry(), setEntry(), updateEntry(), deleteEntry()
  /**
   * Watches the whole library, ordered by name.
   *
   * Deliberately unfiltered: space/type/status/text filtering happens client-side. The library is
   * a curated set in the dozens-to-low-hundreds, so one subscription filtered in memory keeps the
   * filter UI instant and needs no composite indexes — a filter change is a re-render, not a
   * round trip.
   */
  entriesAtom() {
    return atomWithQuery(
      () => query14(typedCollection(equipmentTestLibraryCollection()), orderBy10("name")),
      { schema: EquipmentTestLibraryEntrySchema, log: this.log }
    );
  }
  /**
   * Watches a single entry.
   */
  entryAtom(entryId) {
    return atomWithDocument(
      async (get) => {
        const entryIdValue = await unwrapValue(get, entryId);
        if (entryIdValue) {
          return typedDoc(equipmentTestLibraryEntryPath(entryIdValue));
        }
      },
      { schema: EquipmentTestLibraryEntrySchema, log: this.log }
    );
  }
};

// src/dao/EquipmentTypeCandidateDao.ts
import {
  BaseEquipmentTypeCandidateDao,
  equipmentTypeCandidatePath,
  equipmentTypeCandidatesCollection
} from "@rock/dao";
import { EquipmentTypeCandidateSchema } from "@rock/types";
import { orderBy as orderBy11, query as query15 } from "firebase/firestore";
var EquipmentTypeCandidateDao = class extends BaseEquipmentTypeCandidateDao {
  // Inherits: getEquipmentTypeCandidate(), setEquipmentTypeCandidate(), updateEquipmentTypeCandidate()
  /**
   * Watches all candidates, ordered by how many homes wanted them (most popular first).
   */
  candidatesAtom() {
    return atomWithQuery(
      () => query15(
        typedCollection(equipmentTypeCandidatesCollection()),
        orderBy11("count", "desc")
      ),
      { schema: EquipmentTypeCandidateSchema, log: this.log }
    );
  }
  /**
   * Watches a single candidate.
   */
  candidateAtom(candidateId) {
    return atomWithDocument(
      async (get) => {
        const candidateIdValue = await unwrapValue(get, candidateId);
        if (candidateIdValue) {
          return typedDoc(equipmentTypeCandidatePath(candidateIdValue));
        }
      },
      { schema: EquipmentTypeCandidateSchema, log: this.log }
    );
  }
};

// src/dao/EquipmentTypeDao.ts
import {
  EquipmentTypeSchema,
  TaskRefreshSchema
} from "@rock/types";
import {
  BaseEquipmentTypeDao,
  computeDraftStatus as computeDraftStatus2,
  EQUIPMENT_TYPE_PRESERVED_ON_PUBLISH,
  equipmentTypePath,
  equipmentTypeRefreshPath,
  equipmentTypesCollection,
  toDraftPath as toDraftPath2
} from "@rock/dao";
import { atom as atom4 } from "jotai";
var EquipmentTypeDao = class extends BaseEquipmentTypeDao {
  // Inherits: getEquipmentType(), setEquipmentType(), updateEquipmentType() from BaseEquipmentTypeDao
  /**
   * Gets an atom that watches the whole EquipmentType catalog (one top-level collection).
   * Per-space table views derive by filtering on the `spaceId` field.
   */
  equipmentTypesAtom() {
    return atomWithQuery(typedCollection(equipmentTypesCollection()), {
      schema: EquipmentTypeSchema,
      log: this.log
    });
  }
  /**
   * Gets an atom that watches a single EquipmentType.
   */
  equipmentTypeAtom(typeId) {
    return atomWithDocument(
      async (get) => {
        const typeIdValue = await unwrapValue(get, typeId);
        if (typeIdValue) {
          return typedDoc(equipmentTypePath(typeIdValue));
        }
      },
      { schema: EquipmentTypeSchema, log: this.log }
    );
  }
  /**
   * Watches a single EquipmentType *draft* (admin working copy in the `_draft/db` mirror). Read-only
   * by convention: stage edits through `setEquipmentTypeDraft()` (the DraftLayer choke point that
   * stamps `_draft: true`), never by setting this atom. Returns undefined when no draft exists.
   */
  equipmentTypeDraftAtom(typeId) {
    return atomWithDocument(
      async (get) => {
        const typeIdValue = await unwrapValue(get, typeId);
        if (typeIdValue) {
          return typedDoc(toDraftPath2(equipmentTypePath(typeIdValue)));
        }
      },
      { schema: EquipmentTypeSchema, log: this.log }
    );
  }
  /**
   * Watches all EquipmentType drafts (includes never-published, draft-only entities). The mirror
   * holds only work in flight — publish deletes its doc — so this list is exactly "pending work".
   * Merge with `equipmentTypesAtom` for an admin list that shows both live and unpublished types.
   */
  equipmentTypeDraftsAtom() {
    return atomWithQuery(typedCollection(toDraftPath2(equipmentTypesCollection())), {
      schema: EquipmentTypeSchema,
      log: this.log
    });
  }
  /**
   * Reactive draft/publish status for one type, derived from its draft and live docs. Drives the
   * "unpublished changes" badge and Publish gating. Uses the same preserved-field set as publish so
   * enrichment/placement fields don't read as pending changes.
   */
  equipmentTypeDraftStatusAtom(typeId) {
    const draftAtom = this.equipmentTypeDraftAtom(typeId);
    const liveAtom = this.equipmentTypeAtom(typeId);
    return atom4(async (get) => {
      const [draft, live] = await Promise.all([get(draftAtom), get(liveAtom)]);
      return computeDraftStatus2(draft, live, [...EQUIPMENT_TYPE_PRESERVED_ON_PUBLISH]);
    });
  }
  /**
   * Gets an atom that watches a single EquipmentType refresh task.
   */
  equipmentTypeRefreshAtom(typeId, refreshId) {
    return atomWithDocument(
      async (get) => {
        const { typeIdValue, refreshIdValue } = await unwrapAll(get, {
          typeIdValue: typeId,
          refreshIdValue: refreshId
        });
        if (typeIdValue && refreshIdValue) {
          return typedDoc(equipmentTypeRefreshPath(typeIdValue, refreshIdValue));
        }
        return void 0;
      },
      { schema: TaskRefreshSchema, log: this.log }
    );
  }
  /**
   * Requests a specs regeneration for a type. A create — the caller mints the id and fires this only
   * when no doc exists at it — so a full `setDoc` is the right write; the worker owns every field
   * after this point.
   */
  async requestEquipmentTypeRefresh(typeId, refresh) {
    const path = equipmentTypeRefreshPath(typeId, refresh.id);
    this.log.info("Requesting EquipmentType refresh", { path, type: refresh.type });
    await this.adapter.setDoc(path, refresh);
  }
};

// src/dao/EquipmentTypeFamilyDao.ts
import { BaseEquipmentTypeFamilyDao, equipmentTypeFamiliesCollection } from "@rock/dao";
import { EquipmentTypeFamilySchema } from "@rock/types";
import { orderBy as orderBy12, query as query16 } from "firebase/firestore";
var EquipmentTypeFamilyDao = class extends BaseEquipmentTypeFamilyDao {
  // Inherits: getEquipmentTypeFamily(), setEquipmentTypeFamily(), deleteEquipmentTypeFamily()
  /** Every family, live — the admin Families tab and the model page's type panel. */
  equipmentTypeFamiliesAtom() {
    return atomWithQuery(
      query16(typedCollection(equipmentTypeFamiliesCollection()), orderBy12("name")),
      { schema: EquipmentTypeFamilySchema, log: this.log }
    );
  }
};

// src/dao/FolderDao.ts
import { BaseFolderDao, foldersCollection } from "@rock/dao";
import { FolderSchema } from "@rock/types";
import { getDocs as getDocs4, query as query17, where as where9 } from "firebase/firestore";
var FolderDao = class extends BaseFolderDao {
  // Inherits: getFolder(), setFolder(), updateFolder(), updateFolderIcon(), appendGuideVisit(),
  // deactivateFolder(). Admin currently only needs the base surface (update-equipment's folder
  // validation reads via getFolder).
  async listFolders(homeId) {
    const snapshot = await getDocs4(
      query17(typedCollection(foldersCollection(homeId)), where9("active", "==", true))
    );
    return snapshot.docs.map((doc3) => parseWithSchema(doc3.data(), FolderSchema, doc3.ref.path, this.log)).filter((folder) => !!folder);
  }
};

// src/dao/GuideDao.ts
import { GuideSchema } from "@rock/types";
import { BaseGuideDao, computeDraftStatus as computeDraftStatus3, guidePath, toDraftPath as toDraftPath3 } from "@rock/dao";
import { deleteDoc as deleteDoc3 } from "firebase/firestore";
import { atom as atom5 } from "jotai";
var GUIDE_DIFF_IGNORE = ["publishedAt"];
var GuideDao = class extends BaseGuideDao {
  // Inherits: getGuide(), setGuide(), updateGuide(), getGuidePreview(), setGuidePreview() from BaseGuideDao
  /** Watches the live guide for a context. */
  guideAtom(context) {
    return atomWithDocument(typedDoc(guidePath(context)), { schema: GuideSchema, log: this.log });
  }
  /** Watches the staged preview guide for a context (its slot in the admin-only `_draft/db` mirror). */
  guidePreviewAtom(context) {
    return atomWithDocument(typedDoc(toDraftPath3(guidePath(context))), {
      schema: GuideSchema,
      log: this.log
    });
  }
  /**
   * Reactive flag: a preview guide is staged that differs from live (or there's no live yet). Drives the
   * "In Preview" badge and gates Publish to Live (you must preview before going live).
   */
  guideStagePendingAtom(context) {
    const previewAtom = this.guidePreviewAtom(context);
    const liveAtom = this.guideAtom(context);
    return atom5(async (get) => {
      const [preview, live] = await Promise.all([get(previewAtom), get(liveAtom)]);
      const status = computeDraftStatus3(preview, live, [...GUIDE_DIFF_IGNORE]);
      return status === "draft-pending" || status === "draft-differs";
    });
  }
  /**
   * Delete a guide.
   */
  async deleteGuide(context) {
    const ref = typedDoc(guidePath(context));
    this.log.info("Deleting guide", { path: ref.path });
    await deleteDoc3(ref);
  }
};

// src/dao/HomeDao.ts
import { BaseHomeDao, homePath, homesCollection } from "@rock/dao";
import { HomeSchema } from "@rock/types";
import { getCountFromServer as getCountFromServer4, limit as limit9, orderBy as orderBy13, query as query18, where as where10 } from "firebase/firestore";
var HomeDao = class extends BaseHomeDao {
  /**
   * Gets an atom that watches all homes filtered by active status, with optional name prefix filter
   * and query limit.
   *
   * Homes belonging to seeded E2E accounts are excluded unless `includeTest` is set, matching
   * `countActiveHomes` so the Homes stat and this list can't disagree. The exclusion is an equality
   * match on `test == false`, not `!= true`: Firestore's inequality operators skip docs missing the
   * field, so `!=` would drop every home written before the 20260812-mark-test-users backfill.
   */
  homesAtom(active, namePrefix, queryLimit, includeTest) {
    return atomWithQuery(
      async (get) => {
        const { activeValue, withTest, prefix, lim } = await unwrapAll(get, {
          activeValue: active,
          withTest: includeTest,
          prefix: namePrefix,
          lim: queryLimit
        });
        const constraints = [where10("active", "==", activeValue ?? true)];
        if (!withTest) {
          constraints.push(where10("test", "==", false));
        }
        if (prefix) {
          constraints.push(where10("name", ">=", prefix));
          constraints.push(where10("name", "<=", `${prefix}\uF8FF`));
        }
        constraints.push(orderBy13("name"));
        if (lim) {
          constraints.push(limit9(lim));
        }
        return query18(typedCollection(homesCollection()), ...constraints);
      },
      {
        schema: HomeSchema,
        log: this.log
      }
    );
  }
  /**
   * Watches the most recently created real (non-test) homes. Docs created before the createdAt
   * backfill are excluded by the orderBy, which is correct: they aren't recent.
   *
   * `test == false` is an equality match rather than `!= true` because Firestore's inequality
   * operators skip docs that lack the field entirely. Needs the `test ASC, createdAt DESC`
   * composite index — this is no longer the auto-indexed bare orderBy it used to be.
   */
  recentHomesAtom(count) {
    return atomWithQuery(
      query18(
        typedCollection(homesCollection()),
        where10("test", "==", false),
        orderBy13("createdAt", "desc"),
        limit9(count)
      ),
      { schema: HomeSchema, log: this.log }
    );
  }
  /** Watches every home one user is a member of (active and inactive — the detail page badges status). */
  homesForMemberAtom(userId) {
    return atomWithQuery(
      async (get) => {
        const userValue = await unwrapValue(get, userId);
        if (userValue) {
          return query18(
            typedCollection(homesCollection()),
            where10("memberIds", "array-contains", userValue)
          );
        }
      },
      { schema: HomeSchema, log: this.log }
    );
  }
  /**
   * Total count of active, real (non-test) homes via a server-side aggregation (one billed read per
   * 1,000 docs, no documents transferred). One-shot, not a live subscription. The `active ASC,
   * test ASC` composite serves this; Firestore could merge single-field indexes instead, but the
   * composite is what the homes list already needs.
   */
  async countActiveHomes() {
    const snapshot = await getCountFromServer4(
      query18(typedCollection(homesCollection()), where10("active", "==", true), where10("test", "==", false))
    );
    return snapshot.data().count;
  }
  /**
   * Gets an atom that watches a single home.
   */
  homeAtom(homeId) {
    return atomWithDocument(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return typedDoc(homePath(homeIdValue));
        }
      },
      { schema: HomeSchema, log: this.log }
    );
  }
};

// src/dao/HomeTaskSubscriptionDao.ts
import { HomeTaskSubscriptionSchema } from "@rock/types";
import {
  BaseHomeTaskSubscriptionDao,
  homeTaskSubscriptionPath,
  homeTaskSubscriptionsCollection,
  taskSubscriptionsCollectionGroupName
} from "@rock/dao";
import { orderBy as orderBy14, query as query19, where as where11 } from "firebase/firestore";
var HomeTaskSubscriptionDao = class extends BaseHomeTaskSubscriptionDao {
  // Inherits: getHomeTaskSubscription(), setHomeTaskSubscription(), updateHomeTaskSubscription() from BaseHomeTaskSubscriptionDao
  /**
   * Gets an atom that watches a single task subscription.
   */
  taskSubscriptionAtom(homeId, subscriptionId) {
    return atomWithDocument(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return typedDoc(homeTaskSubscriptionPath(homeIdValue, subscriptionId));
        }
      },
      { schema: HomeTaskSubscriptionSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches all task subscriptions for a home.
   */
  taskSubscriptionsListAtom(homeId) {
    return atomWithQuery(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return query19(
            typedCollection(homeTaskSubscriptionsCollection(homeIdValue)),
            orderBy14("createdAt", "desc")
          );
        }
      },
      { schema: HomeTaskSubscriptionSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches all task subscriptions for a specific consideration type across all homes.
   */
  taskSubscriptionsForConsiderationTypeAtom(considerationTypeId) {
    return atomWithQuery(
      async (get) => {
        const typeIdValue = await unwrapValue(get, considerationTypeId);
        if (typeIdValue) {
          return query19(
            typedCollectionGroup(taskSubscriptionsCollectionGroupName()),
            where11("considerationTypeId", "==", typeIdValue),
            orderBy14("createdAt", "desc")
          );
        }
      },
      { schema: HomeTaskSubscriptionSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches all task subscriptions for a specific home and consideration type.
   * This is more efficient than fetching all subscriptions for a consideration type and filtering client-side.
   */
  taskSubscriptionsForHomeAndConsiderationTypeAtom(homeId, considerationTypeId) {
    return atomWithQuery(
      async (get) => {
        const { homeIdValue, typeIdValue } = await unwrapAll(get, {
          homeIdValue: homeId,
          typeIdValue: considerationTypeId
        });
        if (homeIdValue && typeIdValue) {
          return query19(
            typedCollection(homeTaskSubscriptionsCollection(homeIdValue)),
            where11("considerationTypeId", "==", typeIdValue),
            orderBy14("createdAt", "desc")
          );
        }
      },
      { schema: HomeTaskSubscriptionSchema, log: this.log }
    );
  }
};

// src/dao/InAppNotificationDao.ts
import { BaseInAppNotificationDao, notificationsCollection } from "@rock/dao";
import { InAppNotificationSchema } from "@rock/types";
import { limit as limit10, orderBy as orderBy15, query as query20 } from "firebase/firestore";
var InAppNotificationDao = class extends BaseInAppNotificationDao {
  // Inherits: getNotification(), setNotification(), updateNotification() from BaseInAppNotificationDao
  /** Watches a user's in-app inbox (the materialized read-model the app renders), newest first. */
  notificationsAtom(userId, count = 50) {
    return atomWithQuery(
      async (get) => {
        const userIdValue = await unwrapValue(get, userId);
        if (userIdValue) {
          return query20(
            typedCollection(notificationsCollection(userIdValue)),
            orderBy15("createdAt", "desc"),
            limit10(count)
          );
        }
      },
      { schema: InAppNotificationSchema, log: this.log }
    );
  }
};

// src/dao/InfoTypeDao.ts
import { InfoTypeSchema } from "@rock/types";
import { BaseInfoTypeDao, infoTypePath, infoTypesCollection } from "@rock/dao";
import { deleteDoc as deleteDoc4 } from "firebase/firestore";
var InfoTypeDao = class extends BaseInfoTypeDao {
  // Inherits: getInfoType(), setInfoType(), updateInfoType() from BaseInfoTypeDao
  /**
   * Gets an atom that watches the whole top-level InfoType catalog. Per-space views
   * derive by filtering on the `spaceId` field.
   */
  infoTypesAtom() {
    return atomWithQuery(async () => typedCollection(infoTypesCollection()), {
      schema: InfoTypeSchema,
      log: this.log
    });
  }
  /**
   * Gets an atom that watches a single InfoType.
   */
  infoTypeAtom(typeId) {
    return atomWithDocument(
      async (get) => {
        const typeIdValue = await unwrapValue(get, typeId);
        if (typeIdValue) {
          return typedDoc(infoTypePath(typeIdValue));
        }
      },
      { schema: InfoTypeSchema, log: this.log }
    );
  }
  /**
   * Delete an InfoType.
   */
  async delete(typeId) {
    const ref = typedDoc(infoTypePath(typeId));
    this.log.info("Deleting InfoType", { path: ref.path });
    await deleteDoc4(ref);
  }
};

// src/dao/LessonDao.ts
import { BaseLessonDao, lessonsCollectionGroupName } from "@rock/dao";
import { LessonSchema } from "@rock/types";
import { orderBy as orderBy16, query as query21, where as where12 } from "firebase/firestore";
var LessonDao = class extends BaseLessonDao {
  // Inherits: getLesson(), setLesson(), updateLesson(), markLessonRead(), completeLesson(),
  // dismissLesson(), scheduleLessonReview() from BaseLessonDao
  /**
   * Every home that has been given this lesson, across all homes.
   *
   * The admin's answer to "did this actually reach anyone" — the question you ask when a lesson
   * has been approved for a week and you want to know whether the evaluation is picking it up.
   *
   * The `orderBy` is load-bearing, not cosmetic. A collection-group query with a bare equality
   * filter demands a COLLECTION_GROUP_ASC single-field index, which a composite index does NOT
   * satisfy; ordering by `createdAt` is what makes the committed `(typeId ASC, createdAt DESC)`
   * composite serve it. Drop the ordering and this fails with FAILED_PRECONDITION in every
   * environment. Same shape as `considerationsForConsiderationTypeAtom`.
   */
  lessonsForTypeAtom(typeId) {
    return atomWithQuery(
      async (get) => {
        const typeIdValue = await unwrapValue(get, typeId);
        if (typeIdValue) {
          return query21(
            typedCollectionGroup(lessonsCollectionGroupName()),
            where12("typeId", "==", typeIdValue),
            orderBy16("createdAt", "desc")
          );
        }
      },
      { schema: LessonSchema, log: this.log }
    );
  }
};

// src/dao/LessonTypeDao.ts
import { BaseLessonTypeDao, lessonTypePath, lessonTypesCollection } from "@rock/dao";
import { LessonTypeSchema } from "@rock/types";
var LessonTypeDao = class extends BaseLessonTypeDao {
  // Inherits: getLessonType(), hasLessonType(), setLessonType(), updateLessonType(), applyLessonTypeSync()
  // from BaseLessonTypeDao, plus the applies-to writes from BaseAppliesToOwnerDao
  /**
   * Every lesson type, unfiltered by status on purpose — the admin exists to see drafts and
   * retired entries, which is the opposite of what the app's catalog query wants.
   */
  lessonTypesAtom() {
    return atomWithQuery(typedCollection(lessonTypesCollection()), {
      schema: LessonTypeSchema,
      log: this.log
    });
  }
  /** Gets an atom that watches a single lesson type. */
  lessonTypeAtom(typeId) {
    return atomWithDocument(
      async (get) => {
        const typeIdValue = await unwrapValue(get, typeId);
        if (typeIdValue) {
          return typedDoc(lessonTypePath(typeIdValue));
        }
      },
      { schema: LessonTypeSchema, log: this.log }
    );
  }
};

// src/dao/ModelDocumentCandidateDao.ts
import {
  BaseModelDocumentCandidateDao,
  modelDocumentCandidatePath,
  modelDocumentCandidatesCollection
} from "@rock/dao";
import { ModelDocumentCandidateSchema } from "@rock/types";
import { deleteField as deleteField3, orderBy as orderBy17, query as query22, updateDoc as updateDoc4 } from "firebase/firestore";
var ModelDocumentCandidateDao = class extends BaseModelDocumentCandidateDao {
  /** Atom watching every candidate for a model, newest observation first. */
  candidatesAtom(modelId) {
    return atomWithQuery(
      async (get) => {
        const modelIdValue = await unwrapValue(get, modelId);
        if (modelIdValue) {
          return query22(
            typedCollection(modelDocumentCandidatesCollection(modelIdValue)),
            orderBy17("triedAt", "desc")
          );
        }
      },
      { schema: ModelDocumentCandidateSchema, log: this.log }
    );
  }
  /** Set / clear the human disposition (publish, veto, restore) without touching the worker's fields. */
  async setDisposition(modelId, candidateId, disposition, publishedDocId) {
    const ref = typedDoc(modelDocumentCandidatePath(modelId, candidateId));
    this.log.info("Setting candidate disposition", { modelId, candidateId, disposition });
    await updateDoc4(ref, {
      disposition: disposition ?? deleteField3(),
      publishedDocId: publishedDocId ?? deleteField3(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
};

// src/dao/RockDocDao.ts
import * as z2 from "zod";
import {
  BaseRockDocDao,
  considerationDocPath,
  considerationDocsCollection,
  lessonDocPath,
  rockDocPath,
  rockDocsCollection,
  taskDocPath,
  taskDocsCollection
} from "@rock/dao";
var RockDocDao = class extends BaseRockDocDao {
  // Inherits: getRockDoc(), setRockDoc(), updateRockDoc(), getConsiderationDoc(), setConsiderationDoc(),
  // updateConsiderationDoc(), getLessonDoc(), setLessonDoc(), updateLessonDoc(), getTaskDoc(),
  // setTaskDoc(), updateTaskDoc() from BaseRockDocDao
  /**
   * Gets an atom that watches all rock docs.
   */
  rockDocsAtom() {
    return atomWithQuery(typedCollection(rockDocsCollection()), {
      schema: z2.custom(),
      log: this.log
    });
  }
  /**
   * Gets an atom that watches a single rock doc.
   */
  rockDocAtom(docId) {
    return atomWithDocument(
      async (get) => {
        const docIdValue = await unwrapValue(get, docId);
        if (docIdValue) {
          return typedDoc(rockDocPath(docIdValue));
        }
      },
      { schema: z2.custom(), log: this.log }
    );
  }
  /**
   * Gets an atom that watches all consideration docs.
   */
  considerationDocsAtom() {
    return atomWithQuery(typedCollection(considerationDocsCollection()), {
      schema: z2.custom(),
      log: this.log
    });
  }
  /**
   * Gets an atom that watches a consideration document.
   */
  considerationDocAtom(docId) {
    return atomWithDocument(
      async (get) => {
        const docIdValue = await unwrapValue(get, docId);
        if (docIdValue) {
          return typedDoc(considerationDocPath(docIdValue));
        }
      },
      { schema: z2.custom(), log: this.log }
    );
  }
  /**
   * Gets an atom that watches a lesson doc — the body the admin renders for a lesson type.
   */
  lessonDocAtom(docId) {
    return atomWithDocument(
      async (get) => {
        const docIdValue = await unwrapValue(get, docId);
        if (docIdValue) {
          return typedDoc(lessonDocPath(docIdValue));
        }
      },
      { schema: z2.custom(), log: this.log }
    );
  }
  /**
   * Gets an atom that watches all task docs.
   */
  taskDocsAtom() {
    return atomWithQuery(typedCollection(taskDocsCollection()), {
      schema: z2.custom(),
      log: this.log
    });
  }
  /**
   * Gets an atom that watches a task document.
   */
  taskDocAtom(docId) {
    return atomWithDocument(
      async (get) => {
        const docIdValue = await unwrapValue(get, docId);
        if (docIdValue) {
          return typedDoc(taskDocPath(docIdValue));
        }
      },
      { schema: z2.custom(), log: this.log }
    );
  }
};

// src/dao/RoomDao.ts
import { RoomSchema } from "@rock/types";
import { BaseRoomDao, roomsCollection } from "@rock/dao";
import { orderBy as orderBy18, query as query23, where as where13 } from "firebase/firestore";
var RoomDao = class extends BaseRoomDao {
  // Inherits: getRoom(), setRoom(), updateRoom() from BaseRoomDao
  /**
   * Gets an atom that watches all active rooms for a home.
   */
  roomsAtom(homeId) {
    return atomWithQuery(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return query23(
            typedCollection(roomsCollection(homeIdValue)),
            where13("active", "==", true),
            orderBy18("name")
          );
        }
      },
      {
        schema: RoomSchema,
        log: this.log
      }
    );
  }
};

// src/dao/SpaceDao.ts
import { SpaceSchema } from "@rock/types";
import { BaseSpaceDao, spacePath, spacesCollection } from "@rock/dao";
import { query as query24, where as where14 } from "firebase/firestore";
var SpaceDao = class extends BaseSpaceDao {
  // Inherits: getSpace(), setSpace(), updateSpace() from BaseSpaceDao
  /**
   * Gets an atom that watches spaces, optionally filtered by published status.
   */
  spacesAtom(filter = "published") {
    if (filter === "all") {
      return atomWithQuery(typedCollection(spacesCollection()), {
        schema: SpaceSchema,
        log: this.log
      });
    }
    return atomWithQuery(query24(typedCollection(spacesCollection()), where14("published", "==", true)), {
      schema: SpaceSchema,
      log: this.log
    });
  }
  /**
   * Gets an atom that watches a single space.
   */
  spaceAtom(spaceId) {
    return atomWithDocument(
      async (get) => {
        const spaceIdValue = await unwrapValue(get, spaceId);
        if (spaceIdValue) {
          return typedDoc(spacePath(spaceIdValue));
        }
      },
      { schema: SpaceSchema, log: this.log }
    );
  }
};

// src/dao/SystemInfoDao.ts
import { SystemInfoSchema } from "@rock/types";
import { BaseSystemInfoDao, systemInfoCollection } from "@rock/dao";
import { query as query25, where as where15 } from "firebase/firestore";
var SystemInfoDao = class extends BaseSystemInfoDao {
  /** Watches a home's active info answers. */
  systemInfoListAtom(homeId) {
    return atomWithQuery(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return query25(
            typedCollection(systemInfoCollection(homeIdValue)),
            where15("active", "==", true)
          );
        }
      },
      { schema: SystemInfoSchema, log: this.log }
    );
  }
};

// src/dao/TaskDao.ts
import { BaseTaskDao, tasksCollection, taskPath } from "@rock/dao";
import { TaskSchema } from "@rock/types";
import { getDocs as getDocs5, limit as limit11, orderBy as orderBy19, query as query26, where as where16 } from "firebase/firestore";
var TaskDao = class extends BaseTaskDao {
  /** Returns every pending task for a subscription, regardless of date. */
  async findPendingTasksForSubscription(homeId, subscriptionId) {
    const snapshot = await getDocs5(
      query26(
        typedCollection(tasksCollection(homeId)),
        where16("subscriptionId", "==", subscriptionId),
        where16("status", "==", "pending"),
        orderBy19("taskDate", "asc")
      )
    );
    const tasks = [];
    for (const doc3 of snapshot.docs) {
      const parsed = parseWithSchema(doc3.data(), TaskSchema, doc3.ref.path, this.log);
      if (parsed) {
        tasks.push(parsed);
      }
    }
    return tasks;
  }
  async findSkippedTasksForSubscription(homeId, subscriptionId) {
    const snapshot = await getDocs5(
      query26(
        typedCollection(tasksCollection(homeId)),
        where16("subscriptionId", "==", subscriptionId),
        where16("status", "==", "skipped"),
        orderBy19("taskDate", "asc")
      )
    );
    const tasks = [];
    for (const doc3 of snapshot.docs) {
      const parsed = parseWithSchema(doc3.data(), TaskSchema, doc3.ref.path, this.log);
      if (parsed) {
        tasks.push(parsed);
      }
    }
    return tasks;
  }
  /** Returns the most recent completed task for a subscription, or undefined if none exist. */
  async findLastCompletedTaskForSubscription(homeId, subscriptionId) {
    const snapshot = await getDocs5(
      query26(
        typedCollection(tasksCollection(homeId)),
        where16("subscriptionId", "==", subscriptionId),
        where16("status", "==", "completed"),
        orderBy19("completedAt", "desc"),
        limit11(1)
      )
    );
    const doc3 = snapshot.docs[0];
    return doc3 ? parseWithSchema(doc3.data(), TaskSchema, doc3.ref.path, this.log) : void 0;
  }
  /**
   * Gets an atom that watches a single task.
   */
  taskAtom(homeId, taskId) {
    return atomWithDocument(
      async (get) => {
        const homeIdValue = await unwrapValue(get, homeId);
        if (homeIdValue) {
          return typedDoc(taskPath(homeIdValue, taskId));
        }
      },
      { schema: TaskSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches all tasks for a specific subscription.
   * Ordered by taskDate descending to show most recent first.
   * @param homeId Home ID to query tasks for
   * @param subscriptionId Subscription ID to filter by
   */
  tasksForSubscriptionAtom(homeId, subscriptionId) {
    return atomWithQuery(
      async (get) => {
        const { homeIdValue, subscriptionIdValue } = await unwrapAll(get, {
          homeIdValue: homeId,
          subscriptionIdValue: subscriptionId
        });
        if (homeIdValue && subscriptionIdValue) {
          return query26(
            typedCollection(tasksCollection(homeIdValue)),
            where16("subscriptionId", "==", subscriptionIdValue),
            orderBy19("taskDate", "desc")
          );
        }
      },
      { schema: TaskSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches tasks for a home, ordered by taskDate descending, with an optional
   * status filter (served by the `(status, taskDate)` composite) and a limit — the task list grows
   * forever as tasks recur, so callers should always bound it.
   */
  tasksForHomeAtom(homeId, status, queryLimit) {
    return atomWithQuery(
      async (get) => {
        const { homeIdValue, statusValue, lim } = await unwrapAll(get, {
          homeIdValue: homeId,
          statusValue: status,
          lim: queryLimit
        });
        if (!homeIdValue) {
          return void 0;
        }
        const constraints = [];
        if (statusValue) {
          constraints.push(where16("status", "==", statusValue));
        }
        constraints.push(orderBy19("taskDate", "desc"));
        if (lim) {
          constraints.push(limit11(lim));
        }
        return query26(typedCollection(tasksCollection(homeIdValue)), ...constraints);
      },
      { schema: TaskSchema, log: this.log }
    );
  }
};

// src/dao/TaskTypeDao.ts
import { TaskTypeSchema } from "@rock/types";
import { BaseTaskTypeDao, taskTypePath, taskTypesCollection } from "@rock/dao";
import { query as query27, where as where17 } from "firebase/firestore";
var TaskTypeDao = class extends BaseTaskTypeDao {
  // Inherits: getTaskType(), setTaskType(), updateTaskType() from BaseTaskTypeDao
  /**
   * Gets an atom that watches all task types.
   */
  taskTypesAtom() {
    return atomWithQuery(typedCollection(taskTypesCollection()), {
      schema: TaskTypeSchema,
      log: this.log
    });
  }
  /**
   * Gets an atom that watches all task types for a consideration type.
   */
  taskTypesForConsiderationTypeAtom(considerationTypeId) {
    return atomWithQuery(
      async (get) => {
        const considerationTypeIdValue = await unwrapValue(get, considerationTypeId);
        if (considerationTypeIdValue) {
          return query27(
            typedCollection(taskTypesCollection()),
            where17("considerationTypeId", "==", considerationTypeIdValue)
          );
        }
      },
      {
        schema: TaskTypeSchema,
        log: this.log
      }
    );
  }
  /**
   * Gets an atom that watches a single task type.
   */
  taskTypeAtom(typeId) {
    return atomWithDocument(
      async (get) => {
        const typeIdValue = await unwrapValue(get, typeId);
        if (typeIdValue) {
          return typedDoc(taskTypePath(typeIdValue));
        }
      },
      { schema: TaskTypeSchema, log: this.log }
    );
  }
};

// src/dao/UserDao.ts
import { BaseUserDao, userPath, usersCollection } from "@rock/dao";
import { UserSchema } from "@rock/types";
import { getCountFromServer as getCountFromServer5, limit as limit12, orderBy as orderBy20, query as query28, where as where18 } from "firebase/firestore";
var UserDao = class extends BaseUserDao {
  /**
   * Gets an atom that watches all users, most recently active first, with an optional email-prefix
   * filter, an optional column `sort`, and a query limit. When filtering, Firestore requires the
   * inequality field to lead the ordering, so results switch to email order (same shape as HomeDao's
   * name-prefix filter) and only that column's direction survives `sort`.
   *
   * Seeded E2E accounts are excluded unless `includeTest` is set. The exclusion is an equality
   * match on `test == false`, not `!= true`: Firestore's inequality operators skip docs missing the
   * field, so `!=` would drop every user written before the 20260812-mark-test-users backfill.
   */
  usersAtom(queryLimit, emailPrefix, includeTest, sort) {
    return atomWithQuery(
      async (get) => {
        const { sortValue, withTest, prefix, lim } = await unwrapAll(get, {
          sortValue: sort,
          withTest: includeTest,
          prefix: emailPrefix,
          lim: queryLimit
        });
        const constraints = [];
        if (!withTest) {
          constraints.push(where18("test", "==", false));
        }
        if (prefix) {
          constraints.push(where18("email", ">=", prefix));
          constraints.push(where18("email", "<=", `${prefix}\uF8FF`));
          constraints.push(orderBy20("email", sortValue?.field === "email" ? sortValue.dir : "asc"));
        } else {
          constraints.push(sortConstraint(sortValue, "lastLoginAt", "desc"));
        }
        if (lim) {
          constraints.push(limit12(lim));
        }
        return query28(typedCollection(usersCollection()), ...constraints);
      },
      { schema: UserSchema, log: this.log }
    );
  }
  /**
   * Watches the most recently active real (non-test) users. `lastLoginAt` is stamped on every
   * login, so unlike `createdAt` no doc is missing the field and dropped by the orderBy.
   *
   * `test == false` is an equality match rather than `!= true` because Firestore's inequality
   * operators skip docs that lack the field entirely. Needs the `test ASC, lastLoginAt DESC`
   * composite index — this is no longer the auto-indexed bare orderBy it used to be.
   */
  recentlyActiveUsersAtom(count) {
    return atomWithQuery(
      query28(
        typedCollection(usersCollection()),
        where18("test", "==", false),
        orderBy20("lastLoginAt", "desc"),
        limit12(count)
      ),
      { schema: UserSchema, log: this.log }
    );
  }
  /**
   * Total real (non-test) user docs. One-shot aggregation, not live. The lone equality filter rides
   * the automatic single-field index on `test`, so this needs no composite.
   */
  async countUsers() {
    const snapshot = await getCountFromServer5(
      query28(typedCollection(usersCollection()), where18("test", "==", false))
    );
    return snapshot.data().count;
  }
  /**
   * Gets an atom that watches a single user.
   */
  userAtom(userId) {
    return atomWithDocument(
      async (get) => {
        const userIdValue = await unwrapValue(get, userId);
        if (userIdValue) {
          return typedDoc(userPath(userIdValue));
        }
      },
      { schema: UserSchema, log: this.log }
    );
  }
};

// src/dao/UserLimitsDao.ts
import { BaseUserLimitsDao, userLimitsPath } from "@rock/dao";
import { UserLimitsSchema } from "@rock/types";
var UserLimitsDao = class extends BaseUserLimitsDao {
  // Inherits: getUserLimits(), getLimitsConfig(), getLimit() from BaseUserLimitsDao
  /** Watches one user's usage counters. Resolves undefined until the user first hits a limited action. */
  userLimitsAtom(userId) {
    return atomWithDocument(
      async (get) => {
        const userIdValue = await unwrapValue(get, userId);
        if (userIdValue) {
          return typedDoc(userLimitsPath(userIdValue));
        }
      },
      { schema: UserLimitsSchema, log: this.log }
    );
  }
};

// src/dao/UserNotificationDao.ts
import { BaseUserNotificationDao, userNotificationsCollection } from "@rock/dao";
import { UserNotificationSchema } from "@rock/types";
import { limit as limit13, orderBy as orderBy21, query as query29, where as where19 } from "firebase/firestore";
var UserNotificationDao = class extends BaseUserNotificationDao {
  // Inherits: getUserNotification(), createUserNotification(), receipt setters from BaseUserNotificationDao
  /** Watches one user's notification bus events, newest first. */
  forUserAtom(userId, count = 50) {
    return atomWithQuery(
      async (get) => {
        const userIdValue = await unwrapValue(get, userId);
        if (userIdValue) {
          return query29(
            typedCollection(userNotificationsCollection()),
            where19("userId", "==", userIdValue),
            orderBy21("createdAt", "desc"),
            limit13(count)
          );
        }
      },
      { schema: UserNotificationSchema, log: this.log }
    );
  }
};

// src/dao/UserRoleDao.ts
import { BaseUserRoleDao, userRolesCollection } from "@rock/dao";
import { UserIdSchema } from "@rock/types";
import { getDocs as getDocs6, onSnapshot as onSnapshot2 } from "firebase/firestore";
import { atom as atom6 } from "jotai";
var toEntries = (snap) => snap.docs.map((d) => ({ userId: UserIdSchema.parse(d.id), roles: d.data() ?? {} }));
var UserRoleDao = class extends BaseUserRoleDao {
  // Inherits: getUserRoles(), isAdmin(), setRole(), setAdmin()
  /**
   * Watches the whole `user-roles` collection. It only holds docs for admins/internal accounts, so it's
   * tiny — listing it gives the full set of role-bearing users to cross-reference against the user list,
   * with no per-row reads. Not a typed domain collection (free-form `{ admin: true, ... }` maps), so it
   * uses a raw snapshot rather than the schema-bound atomWithQuery helper.
   */
  userRolesAtom() {
    const log2 = this.log;
    const ref = collection(userRolesCollection());
    const proxyAtom = atom6(async (get) => {
      get(snapshotEpochAtom);
      const dataAtom = atom6(
        new Promise((resolve, reject) => {
          getDocs6(ref).then((snap) => resolve(toEntries(snap))).catch((err) => {
            log2.error("Error loading user-roles", { err });
            reject(err);
          });
        })
      );
      dataAtom.onMount = (setAtom) => onSnapshot2(
        ref,
        (snap) => setAtom(toEntries(snap)),
        (err) => {
          log2.error("Error watching user-roles", { err });
          const rejected = Promise.reject(err);
          rejected.catch(() => void 0);
          setAtom(rejected);
        }
      );
      return dataAtom;
    });
    return atom6(async (get) => get(await get(proxyAtom)));
  }
};

// src/dao/DomainEventDao.ts
import { DomainEventSchema } from "@rock/types";
import { BaseDao as BaseDao3, domainEventPath, domainEventsCollection } from "@rock/dao";
import { getCountFromServer as getCountFromServer6, limit as limit14, orderBy as orderBy22, query as query30, startAfter } from "firebase/firestore";
var DomainEventDao = class extends BaseDao3 {
  constructor() {
    super("DomainEventDao");
  }
  /**
   * Gets an atom that watches domain events, ordered by occurredAt desc,
   * with cursor-based paging via startAfter.
   */
  domainEventsAtom(pageSize, cursor) {
    return atomWithQuery(
      async (get) => {
        const cursorValue = await unwrapValue(get, cursor);
        const constraints = [orderBy22("occurredAt", "desc"), limit14(pageSize)];
        if (cursorValue) {
          constraints.push(startAfter(cursorValue));
        }
        return query30(typedCollection(domainEventsCollection()), ...constraints);
      },
      { schema: DomainEventSchema, log: this.log }
    );
  }
  /**
   * Returns the total count of domain events via a server-side aggregation query.
   * Billed at 1 read per 1,000 documents counted.
   */
  async countEvents() {
    const snapshot = await getCountFromServer6(typedCollection(domainEventsCollection()));
    return snapshot.data().count;
  }
  /**
   * Gets an atom that watches a single domain event.
   */
  domainEventAtom(eventId) {
    return atomWithDocument(
      async (get) => {
        const eventIdValue = await unwrapValue(get, eventId);
        if (eventIdValue) {
          return typedDoc(domainEventPath(eventIdValue));
        }
      },
      { schema: DomainEventSchema, log: this.log }
    );
  }
};

// src/dao/HomeEvalJobDao.ts
import { HomeEvalJobSchema } from "@rock/types";
import { BaseDao as BaseDao4, homeEvalJobRunPath, homeEvalJobRunsCollection, withExpireAt } from "@rock/dao";
import { limit as limit15, orderBy as orderBy23, query as query31, setDoc as setDoc5 } from "firebase/firestore";
var HomeEvalJobDao = class extends BaseDao4 {
  jobsLimit;
  constructor(jobsLimit = 100) {
    super("HomeEvalJobDao");
    this.jobsLimit = jobsLimit;
  }
  /**
   * Gets an atom that watches home eval jobs, ordered by scheduledAt desc, limited to the configured limit.
   */
  homeEvalJobsAtom() {
    return atomWithQuery(
      () => {
        return query31(
          typedCollection(homeEvalJobRunsCollection()),
          orderBy23("scheduledAt", "desc"),
          limit15(this.jobsLimit)
        );
      },
      { schema: HomeEvalJobSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches a single home eval job.
   */
  homeEvalJobAtom(jobId) {
    return atomWithDocument(
      async (get) => {
        const jobIdValue = await unwrapValue(get, jobId);
        if (jobIdValue) {
          return typedDoc(homeEvalJobRunPath(jobIdValue));
        }
      },
      { schema: HomeEvalJobSchema, log: this.log }
    );
  }
  /**
   * Set (create or replace) a HomeEvalJob.
   * Admin-only operation for manually triggering evaluation jobs.
   */
  async set(jobId, data) {
    const ref = typedDoc(homeEvalJobRunPath(jobId));
    this.log.info("Setting HomeEvalJob", { path: ref.path, data });
    await setDoc5(ref, withExpireAt(data));
  }
  /**
   * Schedule a home eval job for a specific home right now.
   * Creating the document triggers runHomeEvalJob in rock-functions.
   */
  async scheduleHomeEvalJobNow(homeId) {
    const jobId = createId();
    const scheduledAt = (/* @__PURE__ */ new Date()).toISOString();
    const job = HomeEvalJobSchema.parse({
      object: "home-eval-job",
      schema: 1,
      id: jobId,
      homeId,
      attempts: 0,
      scheduledAt,
      jobStatus: "pending"
    });
    const ref = typedDoc(homeEvalJobRunPath(jobId));
    this.log.info("Scheduling home eval job", { path: ref.path, jobId, homeId });
    await setDoc5(ref, withExpireAt(job));
    return jobId;
  }
};

// src/dao/HomeEvalResultDao.ts
import { HomeEvalResultSchema } from "@rock/types";
import { BaseDao as BaseDao5, homeEvalResultsCollection } from "@rock/dao";
import { orderBy as orderBy24, query as query32, where as where20 } from "firebase/firestore";
var HomeEvalResultDao = class extends BaseDao5 {
  constructor() {
    super("HomeEvalResultDao");
  }
  /**
   * Gets an atom that watches home eval results for a consideration type, ordered by createdAt desc.
   */
  homeEvalResultsForConsiderationTypeAtom(considerationTypeId) {
    return atomWithQuery(
      async (get) => {
        const typeIdValue = await unwrapValue(get, considerationTypeId);
        if (typeIdValue) {
          return query32(
            typedCollection(homeEvalResultsCollection()),
            where20("considerationTypeId", "==", typeIdValue),
            orderBy24("createdAt", "desc")
          );
        }
      },
      {
        schema: HomeEvalResultSchema,
        log: this.log
      }
    );
  }
  /**
   * Gets an atom that watches home eval results for a job, ordered by createdAt desc.
   */
  homeEvalResultsForJobAtom(jobId) {
    return atomWithQuery(
      async (get) => {
        const jobIdValue = await unwrapValue(get, jobId);
        if (jobIdValue) {
          return query32(
            typedCollection(homeEvalResultsCollection()),
            where20("jobId", "==", jobIdValue),
            orderBy24("createdAt", "desc")
          );
        }
      },
      {
        schema: HomeEvalResultSchema,
        log: this.log
      }
    );
  }
};

// src/dao/ModelDao.ts
import { ModelSchema } from "@rock/types";
import { BaseDao as BaseDao6, modelPath, modelsCollection } from "@rock/dao";
import {
  FieldPath,
  deleteField as deleteField4,
  getCountFromServer as getCountFromServer7,
  getDocs as getDocs7,
  limit as limit16,
  orderBy as orderBy25,
  query as query33,
  runTransaction as runTransaction2,
  updateDoc as updateDoc5,
  where as where21
} from "firebase/firestore";
var ModelDao = class extends BaseDao6 {
  constructor() {
    super("ModelDao");
  }
  /**
   * Gets an atom that watches all models, sorted by modelCanonical, with an optional query limit, an
   * optional `namePrefix` "starts with" match, an optional documentation-status filter, and an
   * optional column `sort`. The prefix must already be folded into `modelCanonical` space (uppercased,
   * non-alphanumerics stripped — `normalizeIdentifier(raw)`) so it lines up with the stored field.
   *
   * `docStatus` filters on the stored rollup (composite index `(docStatus, modelCanonical)`), which is
   * why the status views are complete rather than "whatever the loaded page happened to contain".
   * Archived models carry `docStatus: 'archived'`, so every other status filter excludes tombstones
   * for free — but the unfiltered view still has to drop them client-side (Firestore has no `!=`
   * that composes with the prefix range).
   *
   * A prefix search PINS the ordering to `modelCanonical` (Firestore requires the range field to lead),
   * so only that column's direction survives — the admin greys out the other headers to match. Sorting
   * on any other field while searching would throw, so the pin is enforced here too.
   */
  modelsAtom(queryLimit, namePrefix, docStatus, sort) {
    return atomWithQuery(
      async (get) => {
        const { sortValue, status, prefix, lim } = await unwrapAll(get, {
          sortValue: sort,
          status: docStatus,
          prefix: namePrefix,
          lim: queryLimit
        });
        const constraints = [];
        if (status && status !== "all") {
          constraints.push(where21("docStatus", "==", status));
        }
        if (prefix) {
          const dir = sortValue?.field === "modelCanonical" ? sortValue.dir : "asc";
          constraints.push(orderBy25("modelCanonical", dir));
          constraints.push(where21("modelCanonical", ">=", prefix));
          constraints.push(where21("modelCanonical", "<=", `${prefix}\uF8FF`));
        } else {
          constraints.push(sortConstraint(sortValue, "modelCanonical"));
        }
        if (lim) {
          constraints.push(limit16(lim));
        }
        return query33(typedCollection(modelsCollection()), ...constraints);
      },
      { schema: ModelSchema, log: this.log }
    );
  }
  /**
   * Models carrying an unconfirmed `brandSuggestion` — the brand review queue. `brandSuggestion.name > ''`
   * is the existence-range trick (see `countArchivedModels`), served by the automatic single-field index,
   * and the inequality field has to lead the ordering. Deliberately its own query rather than a mode on
   * `modelsAtom`: a review queue has to be COMPLETE, not "whatever the paginated page happened to load".
   */
  modelsNeedingBrandReviewAtom(queryLimit) {
    return atomWithQuery(
      async (get) => {
        const lim = queryLimit ? await unwrapValue(get, queryLimit) : void 0;
        if (!lim) {
          return void 0;
        }
        return query33(
          typedCollection(modelsCollection()),
          where21("brandSuggestion.name", ">", ""),
          orderBy25("brandSuggestion.name"),
          limit16(lim)
        );
      },
      { schema: ModelSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches a single model.
   */
  modelAtom(modelId) {
    return atomWithDocument(
      async (get) => {
        const modelIdValue = await unwrapValue(get, modelId);
        if (modelIdValue) {
          return typedDoc(modelPath(modelIdValue));
        }
      },
      { schema: ModelSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches the models waiting on a brand request (`brandSuggestion.requestId`): the
   * request page's preview of what resolving it links, and merges. Served by the automatic single-field
   * index on the map sub-field.
   */
  modelsAwaitingBrandRequestAtom(requestId) {
    return atomWithQuery(
      async (get) => {
        const requestIdValue = await unwrapValue(get, requestId);
        if (requestIdValue) {
          return query33(
            typedCollection(modelsCollection()),
            where21("brandSuggestion.requestId", "==", requestIdValue)
          );
        }
      },
      { schema: ModelSchema, log: this.log }
    );
  }
  /** Every model parked on some brand request, for counting what each pending request holds up. */
  modelsAwaitingAnyBrandRequestAtom() {
    return atomWithQuery(
      async () => query33(typedCollection(modelsCollection()), where21("brandSuggestion.requestId", ">", "")),
      { schema: ModelSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches every model linked to a brand. No orderBy — that would demand a composite
   * index; per-brand sets are small, so callers sort client-side.
   */
  modelsByBrandAtom(brandId) {
    return atomWithQuery(
      async (get) => {
        const brandIdValue = await unwrapValue(get, brandId);
        if (brandIdValue) {
          return query33(typedCollection(modelsCollection()), where21("brandId", "==", brandIdValue));
        }
      },
      { schema: ModelSchema, log: this.log }
    );
  }
  /** Total model docs in the catalog, archived tombstones included (server aggregation, one-shot). */
  async countModels() {
    const snapshot = await getCountFromServer7(typedCollection(modelsCollection()));
    return snapshot.data().count;
  }
  /**
   * Count of archived (tombstoned) models — `archivedAt > ''` is the existence-range trick on the
   * ISO-string field, served by the automatic single-field index. One-shot server aggregation.
   */
  async countArchivedModels() {
    const snapshot = await getCountFromServer7(
      query33(typedCollection(modelsCollection()), where21("archivedAt", ">", ""))
    );
    return snapshot.data().count;
  }
  /**
   * Count of models carrying one documentation-status rollup value — the dashboard's pipeline numbers.
   * Single-field equality, so the automatic index serves it. Counts exactly what the list's matching
   * status filter shows: both read the same stored `docStatus`.
   */
  async countModelsWithDocStatus(docStatus) {
    const snapshot = await getCountFromServer7(
      query33(typedCollection(modelsCollection()), where21("docStatus", "==", docStatus))
    );
    return snapshot.data().count;
  }
  /** Count of models awaiting brand review — same existence range as `modelsNeedingBrandReviewAtom`. */
  async countModelsNeedingBrandReview() {
    const snapshot = await getCountFromServer7(
      query33(typedCollection(modelsCollection()), where21("brandSuggestion.name", ">", ""))
    );
    return snapshot.data().count;
  }
  /**
   * Link (or re-link) ONE document: writes exactly the `documents.{id}` map entry via FieldPath
   * (doc ids are content hashes — the dot-path string form can't express them), so a concurrent worker
   * or admin write to a DIFFERENT link is never clobbered. Never write links through a full-doc
   * `setDoc` — that's the clobber this method exists to prevent. The artifact itself lives in
   * `documents/{id}` (`ModelDocumentDao`).
   */
  async linkDocument(modelId, link) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Linking model document", { modelId, docId: link.id, via: link.via });
    await updateDoc5(ref, new FieldPath("documents", link.id), link, "updatedAt", (/* @__PURE__ */ new Date()).toISOString());
  }
  /**
   * A human marks ONE link superseded by another confirmed document (`null` clears the mark). The mark
   * is a human's, so any system provenance goes with it. Sub-field FieldPath writes for the same reason
   * as `linkDocument` — never clobber sibling entries or the rest of this entry. Callers validate with
   * `assertCanSupersede` first.
   */
  async setDocumentSupersededBy(modelId, docId, supersededBy) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Setting model document supersededBy", { modelId, docId, supersededBy });
    await updateDoc5(
      ref,
      new FieldPath("documents", docId, "supersededBy"),
      supersededBy ?? deleteField4(),
      new FieldPath("documents", docId, "supersededVia"),
      deleteField4(),
      "updatedAt",
      (/* @__PURE__ */ new Date()).toISOString()
    );
  }
  /**
   * Unlink a document and, in the same write, clear every mark that pointed at it — so no reader sees a
   * link superseded by a document the model no longer has (it would stay hidden with nothing current).
   */
  async unlinkDocumentAndUnstrand(modelId, docId) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Unlinking model document and clearing its supersession", { modelId, docId });
    await runTransaction2(db, async (tx) => {
      const model = (await tx.get(ref)).data();
      if (!model?.documents?.[docId]) return;
      const outranked = Object.values(model.documents).flatMap(
        (link) => link && link.id !== docId && link.supersededBy === docId ? [link.id] : []
      );
      tx.update(
        ref,
        new FieldPath("documents", docId),
        deleteField4(),
        ...outranked.flatMap((id) => [
          new FieldPath("documents", id, "supersededBy"),
          deleteField4(),
          new FieldPath("documents", id, "supersededVia"),
          deleteField4()
        ]),
        "updatedAt",
        (/* @__PURE__ */ new Date()).toISOString()
      );
    });
  }
  /**
   * Gets an atom that watches every model linking one document — who a shared document reaches, live as
   * syncs link and unlink it. Same single-field index as `findModelsLinkingDocument`.
   */
  modelsLinkingDocumentAtom(docId) {
    return atomWithQuery(
      () => query33(
        typedCollection(modelsCollection()),
        where21(new FieldPath("documents", docId, "id"), "==", docId)
      ),
      { schema: ModelSchema, log: this.log }
    );
  }
  /**
   * Every model whose `documents` map links the given document — the "is this the last link?" check
   * behind artifact deletion. Doc ids are globally unique content hashes, so the map-field equality is
   * precise; served by the automatic single-field index.
   */
  async findModelsLinkingDocument(docId) {
    const snap = await getDocs7(
      query33(
        typedCollection(modelsCollection()),
        where21(new FieldPath("documents", docId, "id"), "==", docId)
      )
    );
    return snap.docs.map((doc3) => doc3.data());
  }
  /**
   * Every model of a brand whose canonical number starts with `prefix` — the coverage form's
   * matching-models preview (the human's only look at a rule's blast radius before saving). Same
   * composite index (brandId, modelCanonical) as the server-side backfill sweep.
   */
  async findModelsByBrandAndPrefix(brandId, prefix) {
    const snap = await getDocs7(
      query33(
        typedCollection(modelsCollection()),
        where21("brandId", "==", brandId),
        where21("modelCanonical", ">=", prefix),
        where21("modelCanonical", "<=", `${prefix}\uF8FF`)
      )
    );
    return snap.docs.map((doc3) => doc3.data());
  }
  /**
   * Archive (junk identity — tombstone, see the schema field docs) or restore a model. Field patch;
   * clearing must be an explicit `deleteField()` — `{ archivedAt: undefined }` is a silent no-op.
   *
   * Archiving also resolves any pending `brandSuggestion`: declaring the identity junk answers "which
   * brand is this?" more decisively than picking one would. It also keeps the review queue honest —
   * `countModelsNeedingBrandReview` can't exclude archived models (Firestore won't mix that inequality
   * with an absent-field test), so a lingering suggestion would inflate the dashboard against a list
   * that hides archived rows.
   */
  async setModelArchived(modelId, archived) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Setting model archived", { modelId, archived });
    await updateDoc5(ref, {
      archivedAt: archived ? (/* @__PURE__ */ new Date()).toISOString() : deleteField4(),
      // Archiving answers any open verdict: a junk identity is neither parked nor worth revisiting.
      ...archived ? { brandSuggestion: deleteField4(), parked: deleteField4(), revisit: deleteField4() } : {},
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  /**
   * Park the manual hunt on a REAL product with a reason (see `ModelParkSchema`), or lift the park with
   * `null`. Parking answers an open revisit nudge, so it clears `revisit` in the same write; the
   * ambient enqueue skips parked models and the revisit path is what wakes them.
   */
  async setModelParked(modelId, reason) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Setting model parked", { modelId, reason });
    const now = (/* @__PURE__ */ new Date()).toISOString();
    await updateDoc5(ref, {
      parked: reason ? { reason, at: now } : deleteField4(),
      ...reason ? { revisit: deleteField4() } : {},
      updatedAt: now
    });
  }
  /** Dismiss a revisit nudge without any other verdict — the human looked and nothing changed. */
  async clearModelRevisit(modelId) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Clearing model revisit", { modelId });
    await updateDoc5(ref, { revisit: deleteField4(), updatedAt: (/* @__PURE__ */ new Date()).toISOString() });
  }
  /**
   * Targeted `knownAs` write. Field patch, not a full-doc set: a full-doc write from a stale snapshot
   * would clobber concurrent per-document writes (the worker's) — same reasoning as `publishDocument`.
   */
  async setModelKnownAs(modelId, knownAs) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Setting model knownAs", { modelId, count: knownAs?.length ?? 0 });
    await updateDoc5(ref, { knownAs, updatedAt: (/* @__PURE__ */ new Date()).toISOString() });
  }
  /**
   * Link (or clear, with `null`) the model's catalog brand — the human decision that makes `brandId`
   * authoritative (automated writers only ever FILL an absent `brandId`, never overwrite). Any explicit
   * decision here also resolves any pending `brandSuggestion`, so it is cleared either way.
   */
  async setModelBrand(modelId, brandId, lineId) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Setting model brand", { modelId, brandId, lineId });
    await updateDoc5(ref, {
      brandId: brandId ?? deleteField4(),
      // A line only ever belongs to the brand it was set with: a brand change clears it.
      lineId: brandId && lineId ? lineId : deleteField4(),
      brandSuggestion: deleteField4(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  /**
   * Re-file the model under another catalog type — the admin fix for a mis-filed model, whose units the
   * join's type gate would otherwise split away from it. Coverage and specs follow on the model's next
   * reconcile.
   */
  async setModelType(modelId, typeId) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Setting model type", { modelId, typeId });
    await updateDoc5(ref, { typeId, updatedAt: (/* @__PURE__ */ new Date()).toISOString() });
  }
  /** Dismiss a pending `brandSuggestion` without linking a brand (an explicit `deleteField()` — see above). */
  async clearBrandSuggestion(modelId) {
    const ref = typedDoc(modelPath(modelId));
    this.log.info("Clearing model brand suggestion", { modelId });
    await updateDoc5(ref, { brandSuggestion: deleteField4(), updatedAt: (/* @__PURE__ */ new Date()).toISOString() });
  }
};

// src/dao/ModelSpellingDao.ts
import { BaseModelSpellingDao, modelSpellingPath } from "@rock/dao";
import { ModelSpellingSchema } from "@rock/types";
var ModelSpellingDao = class extends BaseModelSpellingDao {
  // Inherits: getModelSpelling(). Writes go through `ModelDao.resolveOrCreateModel` (db-server).
  /** A number's projection, live — the other models under one spelling key, on a model's page. */
  modelSpellingAtom(spellingKey) {
    return atomWithDocument(
      async (get) => {
        const key = await unwrapValue(get, spellingKey);
        if (key) return typedDoc(modelSpellingPath(key));
      },
      { schema: ModelSpellingSchema, log: this.log }
    );
  }
};

// src/dao/ModelDocumentDao.ts
import { BaseModelDocumentDao, modelDocumentPath, modelDocumentsCollection } from "@rock/dao";
import { ModelDocumentSchema } from "@rock/types";
import { deleteField as deleteField5, documentId, getDocs as getDocs8, limit as limit17, orderBy as orderBy26, query as query34, updateDoc as updateDoc6, where as where22 } from "firebase/firestore";
var IN_QUERY_LIMIT = 30;
var ModelDocumentDao = class extends BaseModelDocumentDao {
  // Inherits: getModelDocument(), setModelDocument(), deleteModelDocument(), setDocumentOpenaiFileId(), loadReplacementChains()
  /** Gets an atom that watches a single `documents/{docId}` artifact. */
  documentAtom(docId) {
    return atomWithDocument(
      async (get) => {
        const docIdValue = await unwrapValue(get, docId);
        if (docIdValue) {
          return typedDoc(modelDocumentPath(docIdValue));
        }
      },
      { schema: ModelDocumentSchema, log: this.log }
    );
  }
  /**
   * Gets an atom over the whole artifact collection, newest-touched first — the admin library view.
   * Capped: the catalog is small, and a library that outgrows one page wants server-side filters, not
   * a silently longer scroll.
   */
  documentsAtom(queryLimit) {
    return atomWithQuery(
      () => query34(
        typedCollection(modelDocumentsCollection()),
        orderBy26("updatedAt", "desc"),
        limit17(queryLimit)
      ),
      { schema: ModelDocumentSchema, log: this.log }
    );
  }
  /** Gets an atom over the documents an admin marked as replaced by `docId` — its earlier revisions. */
  documentsReplacedByAtom(docId) {
    return atomWithQuery(
      () => query34(typedCollection(modelDocumentsCollection()), where22("replacedBy", "==", docId)),
      { schema: ModelDocumentSchema, log: this.log }
    );
  }
  /**
   * One-shot batched point reads for a set of document ids (`in` queries, chunked at the SDK cap).
   * Missing ids are simply absent from the result. Used where a page needs the union of docs behind
   * many models at once (the models-list rollup) rather than a live per-doc subscription.
   */
  async getModelDocumentsByIds(ids) {
    const unique = [...new Set(ids)];
    if (unique.length === 0) return [];
    const collection3 = typedCollection(modelDocumentsCollection());
    const chunks = [];
    for (let i = 0; i < unique.length; i += IN_QUERY_LIMIT) {
      chunks.push(unique.slice(i, i + IN_QUERY_LIMIT));
    }
    const snapshots = await Promise.all(
      chunks.map((chunk) => getDocs8(query34(collection3, where22(documentId(), "in", chunk))))
    );
    return snapshots.flatMap((snap) => snap.docs.map((doc3) => doc3.data()));
  }
  /**
   * Set an artifact's tags (+ optional title override; `undefined` clears it). Tags live on the SHARED
   * artifact — the edit is visible to every model linking it. Field patch, never a full-doc set: a
   * concurrent ingest completion must not be clobbered by a stale tag dialog.
   */
  async setDocumentTags(docId, tags, title) {
    const ref = typedDoc(modelDocumentPath(docId));
    this.log.info("Setting model document tags", { docId, tags, title });
    await updateDoc6(ref, {
      tags,
      title: title ?? deleteField5(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  /**
   * Set what an artifact is for and its COVERAGE rule (`null` clears the rule) in one write — the
   * document's reach. The `syncDocumentCoverage` trigger reacts: projection upkeep, silent backfill onto
   * existing matching models, and unlinking `via: 'coverage'` links the new reach no longer justifies.
   * Field patch for the same reason as `setDocumentTags`.
   */
  async setDocumentReach(docId, reach) {
    const ref = typedDoc(modelDocumentPath(docId));
    this.log.info("Setting model document reach", { docId, ...reach });
    await updateDoc6(ref, {
      brandIds: reach.brandIds,
      typeIds: reach.typeIds,
      coverage: reach.coverage ?? deleteField5(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    });
  }
  /**
   * Mark an artifact as replaced by a newer revision (`null` undoes it). The `syncDocumentCoverage`
   * trigger then re-settles the supersession marks on every model the change touches. Field patch for
   * the same reason as `setDocumentTags`. Callers check the chain with `assertCanReplace` first.
   */
  async setDocumentReplacedBy(docId, replacedBy) {
    const ref = typedDoc(modelDocumentPath(docId));
    this.log.info("Setting model document replacedBy", { docId, replacedBy });
    const now = (/* @__PURE__ */ new Date()).toISOString();
    await updateDoc6(ref, {
      replacedBy: replacedBy ?? deleteField5(),
      replacedAt: replacedBy ? now : deleteField5(),
      updatedAt: now
    });
  }
};

// src/dao/ProcessHowOftenResultDao.ts
import { ProcessHowOftenResultSchema } from "@rock/types";
import { BaseDao as BaseDao7, processHowOftenResultsCollection } from "@rock/dao";
import { orderBy as orderBy27, query as query35, where as where23 } from "firebase/firestore";
var ProcessHowOftenResultDao = class extends BaseDao7 {
  constructor() {
    super("ProcessHowOftenResultDao");
  }
  /** Watches every task type's process how often results, newest first. */
  processHowOftenResultsAtom() {
    return atomWithQuery(
      query35(
        typedCollection(processHowOftenResultsCollection()),
        orderBy27("createdAt", "desc")
      ),
      { schema: ProcessHowOftenResultSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches process how often results for a task type, ordered by createdAt desc.
   */
  processHowOftenResultsForTaskTypeAtom(taskTypeId) {
    return atomWithQuery(
      async (get) => {
        const typeIdValue = await unwrapValue(get, taskTypeId);
        if (typeIdValue) {
          return query35(
            typedCollection(processHowOftenResultsCollection()),
            where23("taskTypeId", "==", typeIdValue),
            orderBy27("createdAt", "desc")
          );
        }
      },
      {
        schema: ProcessHowOftenResultSchema,
        log: this.log
      }
    );
  }
};

// src/dao/RecallDao.ts
import {
  appRecallsStatePath,
  BaseRecallDao,
  RECLASSIFIABLE_RECALL_STATUSES,
  recallPath,
  recallsCollection
} from "@rock/dao";
import { RecallSchema, RecallsStateSchema } from "@rock/types";
import { and, getCountFromServer as getCountFromServer8, limit as limit18, or, orderBy as orderBy28, query as query36, where as where24 } from "firebase/firestore";
var CATALOG_RECALL_STATUSES = ["draft", "approved"];
var AWAITING_TOPIC_APPROVAL_STATUSES = ["classified", "draft"];
var RECALLS_DEFAULT_FILTER = {
  status: "catalog",
  hasMatches: false,
  reReview: false,
  suggestions: false
};
var RecallDao = class extends BaseRecallDao {
  // Inherits the reads and the review transitions (approve / reject / reopen / edits) from BaseRecallDao.
  /** Ingest is the server's; the admin portal only reviews what the sync and the backfill wrote. */
  createRecallWithKey() {
    return Promise.reject(new Error("Recalls are ingested server-side only"));
  }
  /** Gets an atom that watches the recalls list under one filter. */
  recallsAtom(queryLimit, filter) {
    return atomWithQuery(
      async (get) => {
        const { lim, f } = await unwrapAll(get, { lim: queryLimit, f: filter });
        const constraints = recallConstraints(f ?? RECALLS_DEFAULT_FILTER);
        if (lim) constraints.push(limit18(lim));
        return query36(typedCollection(recallsCollection()), ...constraints);
      },
      { schema: RecallSchema, log: this.log }
    );
  }
  recallAtom(recallId) {
    return atomWithDocument(
      async (get) => {
        const id = await unwrapValue(get, recallId);
        if (id) return typedDoc(recallPath(id));
      },
      { schema: RecallSchema, log: this.log }
    );
  }
  recallsStateAtom() {
    return atomWithDocument(typedDoc(appRecallsStatePath()), {
      schema: RecallsStateSchema,
      log: this.log
    });
  }
  /**
   * One topic's recalls in the given statuses, newest publish first — the Topics tab. Composite index
   * (classification.topicIds CONTAINS, status, publishedAt DESC).
   */
  recallsForTopicAtom(topicId, statuses, queryLimit) {
    return atomWithQuery(
      topicRecallsQuery(topicId, statuses, [orderBy28("publishedAt", "desc"), limit18(queryLimit)]),
      {
        schema: RecallSchema,
        log: this.log
      }
    );
  }
  async countRecallsForTopic(topicId, statuses) {
    const snapshot = await getCountFromServer8(topicRecallsQuery(topicId, statuses, []));
    return snapshot.data().count;
  }
  /**
   * Recalls awaiting approval under any of the topics, each counted once however many topics it carries — the
   * Topics tab's count. One `array-contains-any` query per status (the statuses don't overlap, so the counts
   * add up); topics number a dozen, within its 30 values.
   */
  async countRecallsAwaitingTopicApproval(topicIds) {
    if (topicIds.length === 0) return 0;
    const counts = await Promise.all(
      AWAITING_TOPIC_APPROVAL_STATUSES.map(async (status) => {
        const snapshot = await getCountFromServer8(
          query36(
            typedCollection(recallsCollection()),
            where24("classification.topicIds", "array-contains-any", topicIds),
            where24("status", "==", status)
          )
        );
        return snapshot.data().count;
      })
    );
    return counts.reduce((sum, count) => sum + count, 0);
  }
  /** Untyped recalls of one kind still `classified` — what that kind's Process button would reach. */
  async countUnprocessedUntypedOfKind(productKind) {
    const snapshot = await getCountFromServer8(
      query36(
        typedCollection(recallsCollection()),
        where24("status", "==", "classified"),
        where24("typed", "==", false),
        where24("classification.productKind", "==", productKind)
      )
    );
    return snapshot.data().count;
  }
  /**
   * Candidates for `isRecallStuck`, live: the status half of its rule, oldest first. The age cut stays the
   * caller's, at render, so a recall crossing it while the page is open still shows; ordering by age keeps
   * every stuck recall ahead of the fresh ones, inside the limit.
   */
  stuckCandidatesAtom(queryLimit) {
    const candidates = query36(
      typedCollection(recallsCollection()),
      stuckCandidatesFilter(),
      orderBy28("updatedAt"),
      limit18(queryLimit)
    );
    return atomWithQuery(candidates, {
      schema: RecallSchema,
      log: this.log
    });
  }
  async countRecalls() {
    const snapshot = await getCountFromServer8(typedCollection(recallsCollection()));
    return snapshot.data().count;
  }
  async countRecallsWithStatus(status) {
    const snapshot = await getCountFromServer8(
      query36(typedCollection(recallsCollection()), where24("status", "==", status))
    );
    return snapshot.data().count;
  }
  /** Recalls of one status that would reach at least one home — the list's "has matched units" query. */
  async countWithMatches(status) {
    const constraints = recallConstraints({ ...RECALLS_DEFAULT_FILTER, status, hasMatches: true });
    const snapshot = await getCountFromServer8(query36(typedCollection(recallsCollection()), ...constraints));
    return snapshot.data().count;
  }
  /** Recalls the latest reclassify proposed a type or topic for, awaiting an admin's yes or no. */
  async countSuggestionsPending() {
    const snapshot = await getCountFromServer8(
      query36(typedCollection(recallsCollection()), ...suggestionConstraints())
    );
    return snapshot.data().count;
  }
  /** Approved recalls with a re-process waiting (`draftRules` present — its confidence is always a number). */
  async countReReviewPending() {
    const snapshot = await getCountFromServer8(
      query36(typedCollection(recallsCollection()), where24("draftRules.confidence", ">=", 0))
    );
    return snapshot.data().count;
  }
};
function recallConstraints(f) {
  if (f.reReview) return [where24("draftRules.confidence", ">=", 0), orderBy28("draftRules.confidence")];
  if (f.suggestions) return [...suggestionConstraints(), orderBy28("publishedAt", "desc")];
  if (f.untypedKind) {
    return [
      where24("typed", "==", false),
      where24("classification.productKind", "==", f.untypedKind),
      orderBy28("publishedAt", "desc")
    ];
  }
  const constraints = [
    f.status === "catalog" ? where24("status", "in", CATALOG_RECALL_STATUSES) : where24("status", "==", f.status)
  ];
  if (f.hasMatches) constraints.push(where24("dryRun.unitCount", ">", 0), orderBy28("dryRun.unitCount", "desc"));
  constraints.push(orderBy28("publishedAt", "desc"));
  return constraints;
}
function suggestionConstraints() {
  return [where24("classification.hasSuggestions", "==", true), where24("status", "in", RECLASSIFIABLE_RECALL_STATUSES)];
}
function topicRecallsQuery(topicId, statuses, extra) {
  return query36(
    typedCollection(recallsCollection()),
    where24("classification.topicIds", "array-contains", topicId),
    where24("status", "in", statuses),
    ...extra
  );
}
function stuckCandidatesFilter() {
  return or(where24("status", "==", "new"), and(where24("status", "==", "classified"), where24("typed", "==", true)));
}

// src/dao/RecallJobDao.ts
import { BaseRecallJobDao, recallJobsCollection } from "@rock/dao";
import { RecallJobSchema } from "@rock/types";
import { limit as limit19, orderBy as orderBy29, query as query37 } from "firebase/firestore";
var RecallJobDao = class extends BaseRecallJobDao {
  // Inherits: getRecallJob(), setRecallJob() (how admin requests a job), updateRecallJob(), claimRecallJob()
  /** The most recent jobs, newest first — the recalls list header shows the ones still running. */
  recentRecallJobsAtom(count) {
    return atomWithQuery(
      query37(typedCollection(recallJobsCollection()), orderBy29("createdAt", "desc"), limit19(count)),
      { schema: RecallJobSchema, log: this.log }
    );
  }
};

// src/dao/RecallMatchDao.ts
import { BaseRecallMatchDao, recallMatchesCollection } from "@rock/dao";
import { RecallMatchSchema } from "@rock/types";
var RecallMatchDao = class extends BaseRecallMatchDao {
  // Inherits: getRecallMatch(), setRecallMatch(), deleteRecallMatch()
  /** The models an approved recall has matched — the details page's Matches tab. */
  recallMatchesAtom(recallId) {
    return atomWithQuery(
      async (get) => {
        const id = await unwrapValue(get, recallId);
        if (id) return typedCollection(recallMatchesCollection(id));
      },
      { schema: RecallMatchSchema, log: this.log }
    );
  }
};

// src/dao/RecallTopicDao.ts
import { BaseRecallTopicDao, recallTopicsCollection } from "@rock/dao";
import { RecallTopicSchema } from "@rock/types";
import { orderBy as orderBy30, query as query38 } from "firebase/firestore";
var RecallTopicDao = class extends BaseRecallTopicDao {
  // Inherits: getRecallTopic(), setRecallTopic()
  /** Every recall topic in list order, live — the Topics tab and a recall's Topics pills. */
  recallTopicsAtom() {
    return atomWithQuery(query38(typedCollection(recallTopicsCollection()), orderBy30("order")), {
      schema: RecallTopicSchema,
      log: this.log
    });
  }
};

// src/dao/RecallVerdictDao.ts
import { BaseRecallVerdictDao, recallVerdictsCollection, recallVerdictsCollectionGroupName } from "@rock/dao";
import { RecallVerdictSchema } from "@rock/types";
import { getCountFromServer as getCountFromServer9, query as query39, where as where25 } from "firebase/firestore";
var RecallVerdictDao = class extends BaseRecallVerdictDao {
  // Inherits: getRecallVerdict(), setRecallVerdict(), upsertEvaluation(), setResolution(), accept(), reopen(),
  // claimNotification(), releaseNotification(), silence(), withdraw()
  /** Every unit's verdict for one recall, across homes — the Matches tab (collection-group on `recallId`). */
  verdictsForRecallAtom(recallId) {
    return atomWithQuery(
      async (get) => {
        const id = await unwrapValue(get, recallId);
        if (id) {
          return query39(
            typedCollectionGroup(recallVerdictsCollectionGroupName()),
            where25("recallId", "==", id)
          );
        }
      },
      { schema: RecallVerdictSchema, log: this.log }
    );
  }
  /** One unit's verdicts — the admin equipment page's Recalls section. */
  verdictsForEquipmentAtom(homeId, equipmentId) {
    return atomWithQuery(
      async (get) => {
        const { h, e } = await unwrapAll(get, { h: homeId, e: equipmentId });
        if (h && e) {
          return query39(
            typedCollection(recallVerdictsCollection(h)),
            where25("equipmentId", "==", e)
          );
        }
      },
      { schema: RecallVerdictSchema, log: this.log }
    );
  }
  /** Units currently carrying one verdict value, across every real home — the dashboard's impact figures. */
  async countVerdicts(verdict) {
    const snapshot = await getCountFromServer9(
      query39(
        typedCollectionGroup(recallVerdictsCollectionGroupName()),
        where25("test", "==", false),
        where25("verdict", "==", verdict)
      )
    );
    return snapshot.data().count;
  }
  /** Units whose owner has been told, across every real home — the dashboard's "homes notified" figure. */
  async countNotifiedVerdicts() {
    const snapshot = await getCountFromServer9(
      query39(
        typedCollectionGroup(recallVerdictsCollectionGroupName()),
        where25("test", "==", false),
        where25("notifiedAt", "!=", null)
      )
    );
    return snapshot.data().count;
  }
};

// src/dao/SharedNameplateDao.ts
import { BaseSharedNameplateDao, sharedNameplatesCollection } from "@rock/dao";
import { SharedNameplateSchema } from "@rock/types";
import { getDocs as getDocs9, orderBy as orderBy31, query as query40 } from "firebase/firestore";
var SharedNameplateDao = class extends BaseSharedNameplateDao {
  // Inherits: getSharedNameplate(), applySharedNameplate(), applySharedNameplateRemoval()
  /** One-shot: the whole table, for a write that must gate on it as it stands right now. */
  async findAllSharedNameplates() {
    const snap = await getDocs9(typedCollection(sharedNameplatesCollection()));
    return snap.docs.map((doc3) => parseWithSchema(doc3.data(), SharedNameplateSchema, doc3.ref.path, this.log)).filter((nameplate) => !!nameplate);
  }
  /** Every shared nameplate, live — the admin list and the wizard. */
  sharedNameplatesAtom() {
    return atomWithQuery(query40(typedCollection(sharedNameplatesCollection()), orderBy31("key")), {
      schema: SharedNameplateSchema,
      log: this.log
    });
  }
};

// src/dao/RockManualsJobDao.ts
import { BaseRockManualsJobDao, rockManualsJobRunPath, rockManualsJobRunsCollection } from "@rock/dao";
import { RockManualsJobSchema } from "@rock/types";
import { getCountFromServer as getCountFromServer10, limit as limit20, orderBy as orderBy32, query as query41, where as where26 } from "firebase/firestore";
var RockManualsJobDao = class extends BaseRockManualsJobDao {
  // Inherits getRockManualsJob / setRockManualsJob / updateRockManualsJob / markReviewed / reopenForReview
  // / cancelJob.
  /**
   * Gets an atom that watches a single manuals job.
   */
  rockManualsJobAtom(jobId) {
    return atomWithDocument(
      async (get) => {
        const jobIdValue = await unwrapValue(get, jobId);
        if (jobIdValue) {
          return typedDoc(rockManualsJobRunPath(jobIdValue));
        }
      },
      { schema: RockManualsJobSchema, log: this.log }
    );
  }
  /**
   * Atom watching the most recently touched jobs — the whole queue in one query, for a caller that
   * wants raw worker state rather than a per-model rollup. No current subscriber: the models list read
   * this to derive its status column, which is now the stored `Model.docStatus` (see
   * `deriveModelDocStatus`). Kept because "what is the queue doing right now" is a question this DAO
   * should be able to answer; note it's a live listener, so subscribe deliberately.
   */
  rockManualsJobsAtom(count = 500) {
    return atomWithQuery(
      async () => query41(
        typedCollection(rockManualsJobRunsCollection()),
        orderBy32("updatedAt", "desc"),
        limit20(count)
      ),
      { schema: RockManualsJobSchema, log: this.log }
    );
  }
  /**
   * Count of jobs currently in any of the given statuses (one-shot server aggregation; a single-field
   * `in` filter, served by the automatic index) — the honest count of WORKER state. No current
   * caller: the dashboard's models card counts `Model.docStatus` instead, so its numbers match the
   * rows behind its links (a succeeded job on a model with a failed ingest reads as `ingest-failed`
   * there, and used to be the documented skew between the two).
   */
  async countJobsWithStatus(statuses) {
    const snapshot = await getCountFromServer10(
      query41(typedCollection(rockManualsJobRunsCollection()), where26("status", "in", statuses))
    );
    return snapshot.data().count;
  }
};

// src/dao/TaskGenerationJobRunDao.ts
import { TaskGenerationJobRunSchema, TaskSchema as TaskSchema2 } from "@rock/types";
import {
  BaseDao as BaseDao8,
  taskGenerationJobRunPath,
  taskGenerationJobRunsCollection,
  tasksCollectionGroupName,
  withExpireAt as withExpireAt2
} from "@rock/dao";
import { limit as limit21, orderBy as orderBy33, query as query42, setDoc as setDoc6, where as where27 } from "firebase/firestore";
var RUNS_LIMIT = 100;
var TaskGenerationJobRunDao = class extends BaseDao8 {
  constructor() {
    super("TaskGenerationJobRunDao");
  }
  /**
   * Gets an atom that watches task-generation job runs, ordered by requestedAt desc, limited.
   */
  taskGenerationJobRunsAtom() {
    return atomWithQuery(
      () => {
        return query42(
          typedCollection(taskGenerationJobRunsCollection()),
          orderBy33("requestedAt", "desc"),
          limit21(RUNS_LIMIT)
        );
      },
      { schema: TaskGenerationJobRunSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches a single task-generation job run.
   */
  taskGenerationJobRunAtom(runId) {
    return atomWithDocument(
      async (get) => {
        const runIdValue = await unwrapValue(get, runId);
        if (runIdValue) {
          return typedDoc(taskGenerationJobRunPath(runIdValue));
        }
      },
      { schema: TaskGenerationJobRunSchema, log: this.log }
    );
  }
  /**
   * Gets an atom that watches all tasks created by a specific job run,
   * queried via the tasks collection group by taskGenerationJobRunId.
   */
  tasksByJobRunIdAtom(runId) {
    return atomWithQuery(
      async (get) => {
        const runIdValue = await unwrapValue(get, runId);
        if (runIdValue) {
          return query42(
            typedCollectionGroup(tasksCollectionGroupName()),
            where27("taskGenerationJobRunId", "==", runIdValue),
            orderBy33("createdAt", "asc")
          );
        }
      },
      { schema: TaskSchema2, log: this.log }
    );
  }
  /**
   * Schedule a task-generation job run for right now.
   * Creating the document triggers runTaskGenerationJob in rock-functions.
   */
  async scheduleTaskGenerationNow() {
    const runId = createId();
    const requestedAt = (/* @__PURE__ */ new Date()).toISOString();
    const run = TaskGenerationJobRunSchema.decode({
      object: "task-generation-job-run",
      schema: 1,
      id: runId,
      requestedAt,
      status: "pending"
    });
    const ref = typedDoc(taskGenerationJobRunPath(runId));
    this.log.info("Scheduling task-generation job run", { path: ref.path, runId });
    await setDoc6(ref, withExpireAt2(run));
    return runId;
  }
};
export {
  AWAITING_TOPIC_APPROVAL_STATUSES,
  AppDao,
  AppFlowDao,
  AppStatusDao,
  AppliesToDraftResultDao,
  BrandAliasDao,
  BrandDao,
  BrandRequestDao,
  BrandResearchDao,
  BrandSearchIndexDao,
  BrandSourceDao,
  CATALOG_RECALL_STATUSES,
  ChatConversationDao,
  CompanyDao,
  ConsiderationDao,
  ConsiderationTypeDao,
  CustomEquipmentTypeDao,
  DeviceDao,
  DocumentSearchDao,
  DomainEventDao,
  EquipmentDao,
  EquipmentRefreshDao,
  EquipmentTestLibraryDao,
  EquipmentTypeCandidateDao,
  EquipmentTypeDao,
  EquipmentTypeFamilyDao,
  FolderDao,
  GuideDao,
  HomeDao,
  HomeEvalJobDao,
  HomeEvalResultDao,
  HomeTaskSubscriptionDao,
  InAppNotificationDao,
  InfoTypeDao,
  LessonDao,
  LessonTypeDao,
  ModelDao,
  ModelDocumentCandidateDao,
  ModelDocumentDao,
  ModelSpellingDao,
  ProcessHowOftenResultDao,
  RECALLS_DEFAULT_FILTER,
  RecallDao,
  RecallJobDao,
  RecallMatchDao,
  RecallTopicDao,
  RecallVerdictDao,
  RockDocDao,
  RockManualsJobDao,
  RoomDao,
  SharedNameplateDao,
  SpaceDao,
  SystemInfoDao,
  TaskDao,
  TaskGenerationJobRunDao,
  TaskTypeDao,
  UserDao,
  UserLimitsDao,
  UserNotificationDao,
  UserRoleDao,
  atomWithCursor,
  atomWithLoadMore,
  auth,
  clientAdapter,
  createId,
  initializeFirebase,
  isOnline,
  refreshSnapshotsAtom,
  setOfflineLogger,
  setOnlineStatus,
  sortConstraint,
  storage,
  useCursorPaging,
  useLoadMore
};
//# sourceMappingURL=index.js.map